/*
 * THE READER'S SIDE OF THE SOCIAL PLANE — for either renderer.
 *
 * A thin client over the discussion and voice routes. Reads are anonymous; writes and voice tokens
 * carry the signed-in reader's identity, and HOW they carry it is the one thing the two renderers
 * differ on: the web rides the httpOnly session cookie (the browser sends it), a phone sends a
 * bearer token it holds in the keychain. That difference is a single injected `authorize` function,
 * so the client itself — every route, every 401-becomes-sign-in rule — lives once, here, rather than
 * once per renderer. The `fetch` is injected too (structurally the platform `fetch`), so a test
 * drives it with no network.
 */

/** As much of a Response as this reads. The platform Response satisfies it. */
export interface SocialResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}

export interface SocialRequestInit {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  credentials?: 'include' | 'same-origin';
}

export type SocialFetch = (url: string, init?: SocialRequestInit) => Promise<SocialResponse>;

/**
 * How a request is made to carry the signed-in reader: given the base init of a write, it returns
 * it with the session attached. The web adds `credentials: 'include'` (the cookie); a phone adds an
 * `Authorization: Bearer` header. Reads never pass through it.
 */
export type Authorize = (init: SocialRequestInit) => SocialRequestInit;

/** A post as the discussion API returns it. */
export interface PostView {
  id: string;
  nodeId: string;
  authorId: string;
  body: string;
  parentId: string | null;
  createdAt: string;
  editedAt: string | null;
}

/** A minted voice-join token and where to use it. */
export interface VoiceToken {
  token: string;
  url: string;
  room: string;
  identity: string;
  /**
   * Whether the server will honour a moderation call from this reader in this room.
   *
   * The renderer used to work it out from `hostIds`, and the server's rule is wider — a Hall's
   * owner and co-hosts moderate that Hall's rooms without hosting each one (D22), and a site
   * moderator moderates anywhere — so a co-host was shown no controls in a room the server would
   * have let them run. It decides what is OFFERED and nothing else: every call is checked again
   * server-side (D18), so a client that lies about it gains nothing.
   */
  mayModerate?: boolean;
}

/** Where one of a Hall's outbound links points. */
export type HallLinkKind = 'youtube' | 'discord' | 'web' | 'patreon' | 'other';

export interface HallLink {
  kind: HallLinkKind;
  url: string;
  label: string;
}

/** What a member is in a Hall. */
export type MemberRole = 'member' | 'moderator' | 'owner';

/**
 * Whether a membership has been let in yet.
 *
 * `pending` is the whole reason this type exists on the client: every other read filters it out —
 * a roster is the people IN a community — so it appears in exactly one place, which is a reader
 * being told where they themselves stand.
 */
export type MemberStatus = 'pending' | 'active';

/**
 * A leader's community.
 *
 * `links` is rendered plainly and goes directly where it points — never suppressed, ranked down or
 * stripped (COMMUNITY.md D21.2). `bannedIds` is absent: who was turned away is nobody else's
 * business.
 */
export interface HallView {
  id: string;
  slug: string;
  name: string;
  description: string;
  ownerId: string;
  moderatorIds: string[];
  links: HallLink[];
  /** Proof of control of a channel the leader already had — never a judgement about standing. */
  verified: { kind: string; handle: string; verifiedAt: string } | null;
  visibility: 'public' | 'unlisted';
  memberPolicy: 'open' | 'approval';
  memberLimit: number;
  /**
   * How many people are in it — active members only.
   *
   * Published because a Hall page that shows no size cannot say whether a community is three people
   * or three thousand, and rule 3 asks this layer to be worth a leader's while beside the YouTube
   * and Discord they already have. Counted server-side at read time rather than stored, so it
   * cannot drift: a page claiming 400 with 40 people in it is worse than one claiming nothing.
   *
   * A COUNT, never the roster. Names on a public list is the platform-wide social graph D21 refuses,
   * arrived at by accident.
   */
  memberCount: number;
  createdAt: string;
}

/** Where the asking reader stands with one Hall. `none` covers a stranger and anybody signed out. */
export interface HallStanding {
  status: MemberStatus | 'none';
  role: MemberRole | null;
}

/**
 * THE ONE CONTROL A HALL PAGE SHOULD OFFER THIS READER.
 *
 * The page rendered **Join** and **Leave** side by side, to everyone, always: a member was invited
 * to join, a stranger to leave, and the reader who had knocked at an `approval` Hall was shown both
 * and told nothing about waiting. Each of the three is a control that answers 400 when pressed, and
 * a control that cannot work is worse than an absent one — it reads as a broken product rather than
 * a rule.
 *
 *   - `join`    — not in it, or nobody is signed in. The only control that makes sense with no
 *                 account, and the refusal path already offers to sign in.
 *   - `waiting` — knocked at an approval Hall and not yet let in. This is the state that had no
 *                 word anywhere: the leader-facing half of that queue was built and the applicant's
 *                 half was not.
 *   - `leave`   — in it, and free to go (D21.1).
 *   - `none`    — the OWNER, who may not leave their own Hall, because a Hall with nobody
 *                 accountable for it is what the server refuses. Offering it would be a lie in the
 *                 form of a button.
 *
 * Shared so the phone cannot come to a different conclusion about the same reader on the same day.
 */
export function hallControl(standing: HallStanding): 'join' | 'waiting' | 'leave' | 'none' {
  if (standing.role === 'owner') return 'none';
  if (standing.status === 'pending') return 'waiting';
  return standing.status === 'active' ? 'leave' : 'join';
}

/** How full a community is, when the ceiling is a thing worth saying. */
export interface HallPlaces {
  /** Active members, counted server-side at read time so it cannot drift. */
  taken: number;
  /** What the server will refuse the next joiner past. */
  limit: number;
}

/**
 * HOW FULL A COMMUNITY IS — or null when the ceiling would be a number nobody can act on.
 *
 * `memberLimit` is sent in every Hall view and was read by nothing, so a leader learned the ceiling
 * existed by **being refused**: they work a queue of people who knocked, admit one, and are told
 * *"the community is full"* — a moment after knowing would have let them decide differently about
 * who to let in. It is the same defect as the stage's, one layer up, and it is closed the same way.
 *
 * WHO IS SHOWN IT, and why it is two rules rather than one:
 *
 *   - **Leadership, always.** They are the ones who admit, and the only ones the refusal reaches.
 *   - **Anybody, when it is full.** A stranger looking at a community they cannot join should not
 *     have to press the button to find out; at that moment the number is the answer to what they
 *     are about to do. At every other moment it is not, and a ceiling shown beside a community
 *     nobody is close to filling is furniture.
 *
 * NULL RATHER THAN A FLAG, the same convention as `stageSeats`: a surface renders it or renders
 * nothing, and cannot accidentally draw a zero.
 */
export function hallPlaces(hall: HallView, leads: boolean): HallPlaces | null {
  if (!leads && hall.memberCount < hall.memberLimit) return null;
  return { taken: hall.memberCount, limit: hall.memberLimit };
}

export interface HallMemberView {
  userId: string;
  role: MemberRole;
  joinedAt: string;
}

/**
 * A member as their own Hall's LEADERSHIP sees them — with a name to act on.
 *
 * `displayName` is null for a reader who has since deleted their account: a placeholder would be a
 * small lie about a person, and the row still has to work — a ghost can be a stale co-host somebody
 * needs to stand down.
 */
export interface HallManagedMemberView extends HallMemberView {
  displayName: string | null;
  /**
   * WHICH ROW THIS IS, so the list can be walked past its first page.
   *
   * The membership's own id, and it is here rather than on the public `MemberView` for the reason
   * that shape exists at all: what a leader needs to page their own roster is not something to
   * publish for every Hall in the product. `joinedAt` alone cannot be the cursor — two people can
   * join in one millisecond, and a page boundary between them left the second unreachable, on the
   * one list whose rows carry the bar and the co-host controls.
   */
  id: string;
}

/** Every moderation act worth recording — see the server's `MODERATION_ACTIONS`. */
export type ModerationAction =
  | 'mute' | 'onStage' | 'offStage' | 'remove' | 'ban' | 'unban' | 'addHost' | 'silence' | 'closeRoom'
  /* The door (D24) — room-wide, and the most far-reaching thing a co-host can do to a study
     without naming anybody. */
  | 'doorLocked' | 'doorOpened'
  /* The COMMUNITY's door — the only acts here that happen in no room, and the most serious a
     co-host has: a Hall ban reaches every room the Hall holds and outlives all of them. */
  /* Who was GIVEN the authority every act above needs, and who had it taken back. The log held
     what was done and not the delegation itself, so a co-host appeared in it acting with no
     record of how they came to be able to. Owner-only, like the act. */
  | 'moderatorAdded' | 'moderatorRemoved'
  | 'hallBanned' | 'hallUnbanned'
  /* The three whose effect is PUBLIC: they move where a community's name appears. */
  | 'coHostAsked' | 'coHostAccepted' | 'coHostEnded'
  /* The SITE's own switch, under no Hall and in no room — a Hall's leadership can neither do these
     nor see them, and they never appear in a Hall's log. */
  | 'readerSuspended' | 'readerReinstated';

/**
 * One line of a Hall's moderation log, as its leadership reads it.
 *
 * NAMES, not ids — supervising a team means recognising people. `null` where somebody has deleted
 * their account: the row survives them on purpose, because an audit log an account deletion could
 * erase would make "delete your account" a way to erase what you did to other people.
 *
 * `subject` is null for an act done to the ROOM — silencing it, closing it — rather than to anyone.
 */
export interface HallLogEntry {
  id: string;
  /** Null for an act done to the COMMUNITY itself — barring somebody happens in no room. */
  roomId: string | null;
  action: ModerationAction;
  /** ISO-8601, rendered in the reader's own zone like every other time in this product. */
  at: string;
  actor: string | null;
  subject: string | null;
}

export interface NewHall {
  slug: string;
  name: string;
  description?: string;
  /**
   * Whether it is listed in the directory — decided when it is MADE, not a minute later.
   *
   * Absent means `public`, so nothing that already calls this changes. It is here because it was
   * edit-only: every Hall was born listed, so a leader writing the name, the description and the
   * links was in the directory half-built, and the only way out was a second request they had to
   * know to send. A default nobody can decline at the moment of deciding everything else is not a
   * default, it is a window — and nobody can see a window they were never offered.
   */
  visibility?: 'public' | 'unlisted';
}

export interface HallEdits {
  name?: string;
  description?: string;
  links?: { kind: HallLinkKind; url: string; label?: string }[];
  visibility?: 'public' | 'unlisted';
  memberPolicy?: 'open' | 'approval';
}

/**
 * The one-time token to place on the claimed channel. Shown once, to the owner.
 *
 * `instructions` WAS HERE and is gone: the server composed *"Put this text anywhere in the
 * channel description for @x, then check"* on every start, in English, and no client ever
 * rendered it — the web writes that sentence from its own dictionary, in the reader's language,
 * which is the whole point of `hall.challengeLead`. Every test fixture in the repository passed
 * `instructions: ''`, which is what a field nobody reads looks like from the inside.
 *
 * A server sentence shown to a reader is the defect the refusal vocabulary exists to prevent
 * (`REFUSAL_REASONS`): the server does not know who is reading. Keeping the field would have
 * left a trap for the next client, which would have rendered it and shown English in the middle
 * of a Hebrew page.
 */
export interface HallChallenge {
  /** Which platform is being claimed. Unread today — see the sweep's reason, and §0. */
  kind: string;
  handle: string;
  token: string;
}

/**
 * A Discord channel this reader has connected, as they are allowed to see it.
 *
 * **There is no `url` here and there never will be.** The webhook URL is a credential — anyone
 * holding it can post into that channel — so the server stores it and returns it to nobody, not
 * even the reader who supplied it, because a reflected credential is one XSS away from a stolen
 * one. `webhookId` is its public half and is enough to tell two channels apart.
 *
 * The route calls these `bridge/hooks`; everything a reader sees calls them channels.
 */
/**
 * WHY A CHANNEL STOPPED — two situations a leader is currently told one sentence about.
 *
 * A channel retires itself after five consecutive failures, and since `retiresOn` was narrowed only
 * three answers count: **404**, the webhook was deleted in Discord, and **401 / 403**, it was
 * refused. A 429 or a 5xx no longer retires anything — those are our pace and their bad minute —
 * which means "Stopped" now has exactly two causes.
 *
 * They need different things from the leader. A DELETED webhook is gone for ever: its URL will
 * never answer again, so *"reconnect it"* is advice that cannot work, and what they must do is make
 * a new webhook in Discord and connect that one. A REFUSED channel is usually a permission that
 * changed and can be changed back, which is exactly what reconnect is for.
 *
 * Named rather than carried as a number: a status code on a screen is a fact about HTTP, and the
 * person reading this panel runs a community.
 */
export type ChannelStoppedBecause = 'deleted' | 'refused';

/**
 * The reason, from the status that retired it — or null, which is the honest answer twice over: for
 * a channel that stopped before this was recorded, and for one that has not stopped at all.
 */
export function channelStoppedBecause(status: number | null | undefined): ChannelStoppedBecause | null {
  if (status === 404) return 'deleted';
  if (status === 401 || status === 403) return 'refused';
  return null;
}

export interface ChannelView {
  id: string;
  kind: string;
  webhookId: string;
  /** The passages it hears about. **Empty means every passage**, which is one representation. */
  nodeIds: string[];
  active: boolean;
  /**
   * The status that retired it, when one was recorded — read through `channelStoppedBecause`.
   *
   * A number rather than the name, because the server has no business deciding what a leader is
   * told: the same three codes might one day earn three sentences, and that is a decision for the
   * dictionary rather than for a write path.
   */
  stoppedOn?: number | null;
  createdAt: string;
}

/** A channel being connected: the webhook URL, and what it should hear about. */
export interface NewChannel {
  url: string;
  /** Omitted or empty follows everything — the server treats `$size: 0` as "all". */
  nodeIds?: string[];
}

/** How often a scheduled session comes round. */
export type SessionRepeat = 'none' | 'weekly';

export type SessionStatus = 'scheduled' | 'live' | 'ended' | 'cancelled';

/**
 * WHY A STUDY IS NOT HAPPENING — and two of the three are not the owner's own doing.
 *
 * `owner` is somebody cancelling their own; the other two are the server acting on a decision made
 * elsewhere: the community it was held under was deleted, or its owner may no longer open anything
 * in that community. Those two used to leave nothing behind at all — the row vanished from the
 * reader's own list, which filtered to scheduled and live — so a weekly study disappeared with no
 * event, no message, and nothing to read. Same defect as being removed from a room in silence.
 */
export type SessionCancelReason = 'owner' | 'hall-gone' | 'not-permitted';

/**
 * The doors a STUDY may have — two of the room's three (D24).
 *
 * A room offers `invite` as well; a study cannot, because the code is minted per room and a weekly
 * study opens a new room every week. A passphrase is the one a leader can tell a community once.
 */
export type SessionAccess = 'public' | 'passphrase';

/**
 * The sentence to show for a study that is no longer happening, or null if there is nothing to say.
 *
 * Shared rather than written in each renderer for the usual reason: the web and the phone giving
 * different accounts of the same cancellation is a bug nobody would think to look for. Null for a
 * reader's own cancellation — they did it — and null for a study that is still going to happen.
 *
 * AND THE TYPE SAYS SO, which it did not: it answered `SessionCancelReason | null`, a type that
 * still includes `'owner'` — the one value this function is written never to return. A caller that
 * wanted the narrower fact had to re-check it, and the phone did: `if (why === null || why ===
 * 'owner') return []`, a branch that cannot run and that TypeScript needed anyway. **A rule
 * enforced at runtime in one client and absent from the type everybody shares** is the shape this
 * layer keeps finding; removing the dead branch is what made the type say it out loud.
 */
export function cancellationOf(session: SessionView): Exclude<SessionCancelReason, 'owner'> | null {
  if (session.status !== 'cancelled') return null;
  return session.cancelledReason && session.cancelledReason !== 'owner' ? session.cancelledReason : null;
}

/**
 * A scheduled session — a promise that a room will exist on a node at a time.
 *
 * `roomId` is null until it opens, because a session holds no room in advance; when it goes live
 * the room is instantiated the ordinary way and recorded here.
 */
export interface SessionView {
  id: string;
  nodeId: string;
  /** Null for an unnamed session — show the NODE's name, which the renderer already holds. */
  title: string | null;
  /** The Hall whose study this is, or null for the commons. */
  hallId: string | null;
  ownerId: string;
  /** ISO-8601, so it carries an offset: "Thursday 20:00" is 20:00 somewhere. */
  startsAt: string;
  durationMinutes: number;
  repeat: SessionRepeat;
  status: SessionStatus;
  /**
   * Which door the room it opens will have — never the word behind it.
   *
   * Public, so a reader deciding whether to plan their Thursday around a study knows they will be
   * asked for a passphrase. Learning that at the door is arriving at a room you cannot enter.
   */
  access: SessionAccess;
  /** Why, once it is cancelled — read through `cancellationOf`, which drops the owner's own. */
  cancelledReason: SessionCancelReason | null;
  roomId: string | null;
}

/** What a reader asks for when scheduling one. */
export interface NewSession {
  title?: string;
  /**
   * Hold it under a Hall: it appears on that Hall's page, and the room it opens inherits the Hall,
   * so the leader's mod team moderates the study without anyone hosting it by hand. Refused unless
   * the caller leads the Hall or is an active member.
   */
  hallId?: string;
  startsAt: string;
  /**
   * The IANA zone this was scheduled from — `Intl.DateTimeFormat().resolvedOptions().timeZone`.
   *
   * Only a WEEKLY session needs it, and then it decides whether the study keeps its hour: without
   * it the next occurrence is `startsAt + 7 days`, which moves a 20:00 meeting to 19:00 the week
   * the leader's country leaves daylight saving, and leaves it there.
   */
  timeZone?: string;
  durationMinutes?: number;
  repeat?: SessionRepeat;
  /**
   * Which door the room it opens will have (D24) — and only two of the room's three.
   *
   * `invite` is absent on purpose: an invite code is minted per room and a weekly study opens a new
   * room every occurrence, so it would hand a community a different code every week that none of
   * them had been given. A passphrase is the door that survives repetition.
   */
  access?: SessionAccess;
  /** Required when `access` is `passphrase`, at least {@link MIN_PASSPHRASE} characters. */
  passphrase?: string;
}

/** What a reader offers to get into a private room. Both absent for a public one. */
export interface RoomSecrets {
  passphrase?: string;
  inviteCode?: string;
  /**
   * ASK FOR A LISTENER'S TOKEN, whatever this reader would otherwise be granted.
   *
   * Not a preference a reader expresses — a fact the CLIENT establishes: the browser has refused
   * this site a microphone, and a token granting one is a token the SFU will not connect with. It
   * only ever removes capability, so a server honouring it trusts nothing.
   *
   * A listener may still type, which is the whole reason this is worth having: the alternative for
   * a reader who said no to the prompt was not getting into the room at all.
   */
  listenOnly?: boolean;
}

/**
 * IS THERE ANYBODY TO ASK ABOUT? — read from the manifest before asking `auth/me`.
 *
 * A content-only deploy carries no identity plane, so that route does not exist. Clients asked
 * anyway, once per page load, and the browser refused the answer before the app saw it: the request
 * carries credentials, a plane-less server sends no `Access-Control-Allow-Credentials`, and the
 * console error is written by the browser where no `catch` can reach it.
 *
 * `!== false`, NOT `=== true`, and the asymmetry is the whole of the rule. A client newer than the
 * server it is talking to sees no `social` field at all. Reading that silence as "no plane" would
 * hide every account control from readers who are signed in — a far worse failure than one wasted
 * request, and a silent one. Only an explicit `false` stops the ask.
 *
 * Here rather than in the web app because the phone needs the same rule, and a rule that lives in
 * two places is a rule that will hold in one of them.
 */
export function hasIdentityPlane(manifest: { social?: { enabled: boolean } } | undefined): boolean {
  return manifest?.social?.enabled !== false;
}

/** The signed-in reader. `id` is our own user id — never the Google subject. */
export interface Me {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  role: string;
}

/** May everyone in a room talk, or only those on the stage? */
export type RoomPolicy = 'open' | 'stage';

/**
 * Who may come in, IN THE ORDER A FORM SHOULD OFFER THEM — open first, hidden last.
 *
 * A runtime list rather than a union alone, because every client has to draw one control per door
 * and a type cannot be mapped over. Each of them was writing the three out by hand: the web had
 * three `<option>`s AND a ternary parsing the same three strings back, so a fourth door added to
 * the markup and forgotten in the ternary would have offered a door that quietly opened a PUBLIC
 * room — a form claiming a lock it did not fit.
 */
export const ROOM_DOORS = ['public', 'passphrase', 'invite'] as const;

/**
 * Who may come in. `passphrase` is a door you can see and ask to be let through; `invite` is a door
 * you cannot see at all, so those rooms never appear in a node's list.
 *
 * Derived from the list, so the two cannot disagree — the same pairing the server's `ROOM_ACCESS`
 * is held to, made unnecessary here by there being only one copy.
 */
export type RoomAccess = typeof ROOM_DOORS[number];

/**
 * The door named by an untrusted string, or null — what a `<select>` hands back.
 *
 * Every client needs this and the browser gives no help: `event.target.value` is a `string`, so the
 * choice is between a cast, a ternary chain that silently falls through to `public`, and asking the
 * list itself. A door this does not recognise is a door that does not exist, and the caller keeps
 * what it had rather than opening a room with the wrong lock.
 */
export function doorNamed(value: string): RoomAccess | null {
  return ROOM_DOORS.find(door => door === value) ?? null;
}

/**
 * A voice room as the API returns it.
 *
 * NOTE what is not here: `bannedIds`. Who was thrown out of a room is nobody else's business, and
 * the server does not send it — so no renderer can accidentally show it.
 */
export interface RoomView {
  id: string;
  nodeId: string;
  /** Null for a room nobody named — show the NODE's name, which the renderer already holds. */
  title: string | null;
  /** The Hall this was opened under, or null for the commons. An id; the name comes from the Hall. */
  hallId: string | null;
  access: RoomAccess;
  /** Present only in a host's own view of an invite room — it is the whole of that room's secret. */
  inviteCode?: string;
  /**
   * Whether the READER WHO ASKED may moderate this room — present only when one room is read,
   * and only when the server knows who is asking.
   *
   * The same answer the join token carries, from the same rule, and it is here because a token
   * is minted ONCE. A Hall's owner appointing a co-host while that co-host is sitting in one of
   * the Hall's rooms is the D22 story at its most urgent — a room is going wrong and a leader
   * reaches for a second pair of hands — and the appointee saw nothing until they left and came
   * back, because nothing re-decides a token and `hostIds` never named them.
   *
   * Not on the LIST, which is the same line `mint` already carries: a Hall read per room is a
   * page of queries to answer a question only the person in the room is asking.
   */
  mayModerate?: boolean;
  hostIds: string[];
  speakerIds: string[];
  /**
   * Who is asking to speak, in the order they asked (COMMUNITY.md D22).
   *
   * The order is the feature: a queue is a promise that asking first means being asked first, and
   * that is the whole difference between a raised hand and shouting louder.
   */
  raisedIds: string[];
  /**
   * A second Hall co-hosting this room, proposed or accepted (COMMUNITY.md D22).
   *
   * `acceptedAt` null is an invitation nobody has answered. It attaches no name to anything: both
   * the Hall listings and the moderation gate require the acceptance, so a proposal is a question
   * and not a claim.
   */
  coHall: { hallId: string; acceptedAt: string | null } | null;
  policy: RoomPolicy;
  stageLimit: number;
  capacity: number;
  video: boolean;
  status: 'live' | 'closed';
  occupancy: number;
  createdAt: string;
}

/**
 * WHETHER TO ASK BEFORE TRYING — the rule every client needs and only one of them had.
 *
 * The mint answers **403 for everything**: banned, closed, wrong passphrase, invite-only. That is
 * deliberate, because a 403/404 split on a secret turns guessing into an oracle for which rooms
 * exist. The cost of that decision is that a refusal cannot be explained AFTER the fact, so a
 * client has to ask BEFORE — and it can, because `access` is public on the room.
 *
 * A client that does not ask tells a reader **"this room is not open to you"** about a room they
 * could walk into by typing six letters. That is not a vague message; it is a false one, and the
 * phone gave it for as long as passphrase rooms have existed.
 *
 * A HOST IS NEVER ASKED. The server exempts them at the mint, precisely so somebody who set a
 * passphrase and forgot it is not locked out of the room they moderate — and a client that asks
 * anyway would lock them out of their own room a second before the server let them in.
 */
export function mustBeAsked(room: RoomView, meId: string | null): 'passphrase' | 'invite' | null {
  if (meId !== null && room.hostIds.includes(meId)) return null;
  if (room.access === 'passphrase') return 'passphrase';
  /* An invite room is entered by its CODE rather than by naming its id, so a listed one — a host
     sees their own — cannot be joined from the list at all. */
  if (room.access === 'invite') return 'invite';
  return null;
}

/**
 * WHETHER TO OFFER THIS READER THE ROOM'S CONTROLS.
 *
 * `mayModerate` is the SERVER's answer and arrives with the token, because no client can work it
 * out: a Hall's owner and co-hosts moderate that Hall's rooms without hosting each one (D22), and a
 * site moderator moderates anywhere — none of them appear in `hostIds`. A client deciding from
 * `hostIds` alone shows a co-host NOTHING while the server stands ready to honour every action they
 * cannot reach, which is the delegation D22 calls the point of a Hall meeting nothing at the end.
 *
 * EITHER ANSWER IS ENOUGH, and it used to be `??` — which is a different rule.
 *
 * `??` falls through only for a missing field, so a server that DID answer, with `false`, settled
 * it for the life of the connection. The token is minted once and never re-decided; the room is
 * re-read on every nudge. So a reader handed hostship **while they are in the room** — which is
 * exactly what "share hostship" is for, and the moment a host reaches for it is the moment the
 * room needs a second pair of hands — watched `hostIds` gain their own name and no control
 * appear, until they left and came back.
 *
 * `hostIds` is not a guess. It arrives from the server on every read, and anybody in it would be
 * granted `mayModerate` by the very next mint, so the two answers cannot disagree about them. The
 * old reading is kept for the case it was written for: a server that mints no field at all.
 *
 * The other direction is safe and stays: somebody stood down mid-room keeps the controls OFFERED
 * until they re-read, presses one, and is told they are not a host. Offering too much is checked
 * again server-side (D18); offering too little cannot be checked by anybody.
 *
 * AND BOTH ANSWERS ARE READ HERE, rather than by whichever client remembered to.
 *
 * There are two: the one minted with the token, and `room.mayModerate`, which `GET /rooms/:id`
 * answers on every re-read. The web merged them at its own call site and the phone did not — so
 * the appointment this rule exists for reached one client and not the other, which is a shape
 * this project has shipped before and has a test for elsewhere. A third client would have had to
 * remember too.
 *
 * The ROOM wins where it answers, because a token is minted once and a room is read again. Only
 * `findOne` fills the field — a list omits it deliberately, since a Hall read per room is a page
 * of queries to answer a question only the person in the room is asking — so everywhere else it
 * is absent and the token still decides.
 */
export function mayRunRoom(room: RoomView, meId: string | null, mayModerate?: boolean): boolean {
  return (room.mayModerate ?? mayModerate ?? false) || (meId !== null && room.hostIds.includes(meId));
}

/**
 * DOES THIS READER LEAD THIS COMMUNITY — the server's own line, written client-side.
 *
 * `halls.controller` decides it in one private helper:
 *
 *     const leads = hall.ownerId.equals(user._id) || hall.moderatorIds.some(m => m.equals(user._id));
 *
 * and everything a leader may reach is behind it — the roster, the record, the queue at the door,
 * the co-host requests, barring somebody from the community. The web wrote the same sentence
 * inline on its Hall page, which was fine while there was one Hall page.
 *
 * It is here for the reason `rosterFor` and `postVerbs` are: **a rule copied into two clients is a
 * rule that will differ in one of them**, and the phone has no Hall page YET (§0 carries it as
 * open). Stating it once now means the second client cannot come to a different answer about who
 * runs a community — which is the answer that decides whether somebody is shown a moderation
 * record at all.
 *
 * OWNERSHIP IS SEPARATE, and not a courtesy: a co-host may bar somebody and read the log, and may
 * NOT appoint another co-host, delete the Hall, or take its export. The server refuses each of
 * those to anybody but the owner, so a client that offered them would be offering a refusal.
 */
export function leadsHall(hall: HallView | null, meId: string | null): boolean {
  return ownsHall(hall, meId) || (hall !== null && meId !== null && hall.moderatorIds.includes(meId));
}

/** Whether this reader OWNS it — the narrower half of the rule above, and the one that appoints. */
export function ownsHall(hall: HallView | null, meId: string | null): boolean {
  return hall !== null && meId !== null && hall.ownerId === meId;
}

/**
 * IS THE ROOM QUIET — everyone but its hosts holding a listener's grant?
 *
 * `silenceRoom` does two things (see it below): it empties the stage and puts the room ON the
 * stage, so the quiet survives the next arrival. Those two together are the state, and reading
 * them back is the only way a client can SAY it happened.
 *
 * That matters more than it sounds. On the web a host watches the participants' badges change and
 * infers it worked. A phone has no roster to watch — the roster comes from the voice session, and
 * the phone has no audio yet — so without this, silencing a room there is a button that produces
 * no visible change at all, which is indistinguishable from a button that does nothing.
 *
 * A rule both clients need, so it is not written twice: `stageLimit` is deliberately not consulted,
 * because a stage of eight with nobody on it is still a quiet room.
 */
export function roomIsQuiet(room: RoomView): boolean {
  return room.policy === 'stage' && room.speakerIds.length === 0;
}

/** How full a room's stage is. */
export interface StageSeats {
  /** Promoted speakers right now. */
  taken: number;
  /** What the server will refuse a promotion past — `stageLimit`, said once. */
  limit: number;
}

/**
 * HOW FULL THE STAGE IS, or null in a room that has no stage.
 *
 * `stageLimit` is sent to every client in every room view and was read by nobody — one of five
 * fields §0 records as *"a product gap wearing the costume of a spare field"*. The gap is that the
 * ceiling was learned only by hitting it: a host works a queue of raised hands, promotes, and is
 * told *"the stage is full (8)"* by the refusal. Nothing said so a moment earlier, when they could
 * have taken somebody off first; and a listener deciding whether to bother asking could not see
 * whether there was anywhere to be promoted TO.
 *
 * NULL UNDER `open` POLICY, and that is the whole reason this is a function rather than two field
 * reads at each call site. In an open room everybody may speak, `speakerIds` decides nothing, and a
 * seat count would be a number about a mechanism that is not running — which is worse than absent,
 * because it looks like a limit somebody is subject to.
 *
 * HOSTS ARE NOT COUNTED, because the server does not count them: the cap is enforced in the query
 * as `$size: '$speakerIds'` against `stageLimit`, and a host's grant comes from `hostIds`. A count
 * that included them would say "8 of 8" in a room where the server will happily promote a ninth
 * person — the same defect as the seat held by somebody who left, which §0 already records.
 */
export function stageSeats(room: RoomView): StageSeats | null {
  if (room.policy !== 'stage') return null;
  return { taken: room.speakerIds.length, limit: room.stageLimit };
}

/**
 * A control over the door of a room already open, and the door it moves to (D24).
 *
 * `kind` is what a reader is being offered; `to` is what gets sent. They are separate because two of
 * the three send the SAME access — locking and rotating are both `invite`, and only the wording
 * tells a host which one they just pressed.
 */
export interface DoorControl {
  kind: 'lock' | 'open' | 'rotate';
  to: RoomAccess;
}

/**
 * WHICH DOOR CONTROLS A HOST GETS, given the door the room has now (D24).
 *
 * The answer to the bad day this layer is most likely to have. A raided room can be defended one
 * arrival at a time with ban and remove, or by ending it — silence and close both stop the study
 * along with the raid. Locking the door stops the ARRIVING, which is the half that scales, and
 * leaves the conversation running.
 *
 * Three rules, and each of them was written into a client by hand before it was written here:
 *
 * 1. **Locking goes to `invite`, never to `passphrase`.** In the minute this control exists for
 *    there is no time to choose a door or think of a word, so the mode that needs no typing is the
 *    only one reachable in a hurry.
 * 2. **Rotation is offered for `invite` alone.** Pressing lock again mints a fresh code, which is
 *    the same act as rotating one that ended up somewhere public. A passphrase is a word the leader
 *    chose and typed; replacing it needs that typing, so a client without a box for it must not
 *    quietly mint an invite code over a door readers were told needs a word.
 * 3. **Any locked door can be opened again**, or D24's lock is a one-way door: a study that shut
 *    itself for one bad hour would stay shut, and a leader would have to close and re-open the room
 *    — losing everyone in it — to undo a thing that was meant to be temporary.
 *
 * Shared because the phone needed the same three and the web already had them: the client that
 * forgets one leaves a host with only the controls that end the study, which is how this was
 * MISSING from the phone entirely — the one a leader is most likely to be holding when a room goes
 * wrong, since a phone is what is in your hand and the panel is what you have when you are already
 * sitting down.
 */
export function doorControls(room: RoomView): DoorControl[] {
  if (room.access === 'public') return [{ kind: 'lock', to: 'invite' }];
  return [
    ...(room.access === 'invite' ? [{ kind: 'rotate' as const, to: 'invite' as const }] : []),
    { kind: 'open' as const, to: 'public' as const },
  ];
}

/**
 * How near a study is, when it is near enough to matter — or null when it is not.
 *
 * Both clients render an instant: *"Thu 21 Aug, 20:00"*, correct in the reader's own zone and
 * useless in the ten minutes that decide whether they turn up. A reader looking at a study starting
 * shortly has to compare that string against their own clock, work out whether it is even today,
 * and do it while deciding something else — and `GROWTH.md §4` calls calendared activity the
 * scarcest thing this product has. The whole point of a session is that somebody arrives.
 *
 * AN HOUR IS THE WINDOW. Beyond it the date is the useful fact and a countdown is noise; inside it
 * the countdown is the only fact. Zero means it is due now, which is honest for the seconds between
 * the promised time and the opener's next tick — a study is claimed within thirty seconds of its
 * hour, and "in 0 minutes" is what those seconds actually are.
 *
 * Returns the NUMBER and not a phrase, because every client says it in three languages and a
 * sentence built here would be one of them.
 */
export const SOON_MINUTES = 60;

export function startsSoon(startsAt: string, now: Date = new Date()): number | null {
  const at = new Date(startsAt);
  if (Number.isNaN(at.getTime())) return null;

  const minutes = Math.round((at.getTime() - now.getTime()) / 60_000);
  if (minutes > SOON_MINUTES) return null;
  /* A study whose hour has passed and which has not opened yet is "now" rather than a negative
     number: the opener is about to claim it, and counting up from a missed start would be a clock
     telling a reader they are late for something that has not begun. */
  return Math.max(0, minutes);
}

/** What a connected channel is listening to — or that it has stopped. */
export type ChannelFollows = 'retired' | 'all' | 'thisPassage' | 'somePassages';

/**
 * WHAT A CHANNEL IS DOING, in four states — and the ORDER is the whole point.
 *
 * `retired` is asked FIRST, and that is not tidiness. A channel retires itself after five
 * consecutive failures, which is exactly what a webhook deleted in Discord looks like: 404 for
 * ever. Retiring is right — retrying for ever turns a bridge into an outbound-request generator
 * aimed at somebody else's server — but the server then skips it, so it announces NOTHING while a
 * panel that only looked at `nodeIds` showed it exactly as it showed a working one. The server had
 * computed `active: false` and said so in the view all along; the client simply never read it, and
 * a leader whose community had stopped hearing about rooms had no way to find out.
 *
 * Shared because the phone needed the same four states, and an ordering rule is the easiest kind
 * to lose in a second copy: every branch below still returns something plausible if `active` is
 * checked last, which is a bug that looks like working software.
 *
 * An EMPTY `nodeIds` means every passage — one representation, matching the server's `$size: 0`.
 */
export function channelFollows(channel: ChannelView, nodeId: string): ChannelFollows {
  if (!channel.active) return 'retired';
  if (channel.nodeIds.length === 0) return 'all';
  if (channel.nodeIds.includes(nodeId)) return 'thisPassage';
  return 'somePassages';
}

/** What a form has collected about a room somebody wants to open. */
export interface RoomFields {
  title: string;
  /** The Hall to open it under, or absent/empty for the commons. */
  hallId?: string;
  access: RoomAccess;
  /** Whatever is in the passphrase box, which is not the same as what gets sent. */
  passphrase?: string;
}

/**
 * THE ROOM A FORM IS DESCRIBING — or null when it is not describing one yet.
 *
 * Two rules, both of which were written out separately in each client:
 *
 * 1. **A passphrase belongs to exactly one door.** It is sent for `passphrase` and for nothing
 *    else — so a reader who types one, thinks better of it and switches to "Anyone" does not
 *    quietly ship the words they typed to a room that has no use for them.
 * 2. **A passphrase room needs a passphrase of at least {@link MIN_PASSPHRASE}.** The server's DTO
 *    says `@MinLength(4)` — "a one-character passphrase is a lock with the key in it" — and a
 *    refusal for something the form can see for itself is a wasted round trip and a worse sentence
 *    than an unavailable button.
 *
 * That number is why this is shared rather than convenient. The web had `< 4` written into a
 * `disabled` expression and the phone, being written later, would have had `> 0`: the same door,
 * two different locks, and the weaker one on the client most likely to be used in public. A
 * minimum length is exactly the kind of constant that gets copied once and then updated once.
 *
 * `hallId` empty becomes ABSENT rather than empty-string: the DTO distinguishes a missing field
 * from an explicit one, and the commons is the missing case.
 */
export const MIN_PASSPHRASE = 4;

/*
 * WHAT A HALL'S ADDRESS MAY BE — moved here from the server's schema, because the client that
 * DERIVES one could not ask.
 *
 * A leader types the name of their community and the address is made from it. The derivation keeps
 * lowercase letters, digits and hyphens and drops everything else — so a name written in Hebrew or
 * in Arabic derives to an EMPTY STRING, and the form, finding no address, returned silently. The
 * button was live, the leader pressed it, and nothing happened at all: no message, no request, no
 * reason. In a product that reads in three languages, for the first act a community leader ever
 * performs.
 *
 * The reserved list has the same shape of problem in a smaller way: "Support", "Help" and "About"
 * are ordinary things to call a community, and the server refuses their addresses. The client can
 * say so now, before the press, instead of the server saying it afterwards.
 */
/**
 * Slugs that would collide with a route or impersonate the product.
 *
 * A Hall at `/h/admin` or `/h/api` is a support incident waiting to happen, and one at
 * `/h/versesverse` is impersonation of us rather than of another leader — which §3.1 cares about
 * for the same reason it cares about the other kind.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'admin', 'api', 'app', 'auth', 'new', 'edit', 'settings', 'support', 'help', 'about',
  'versesverse', 'verses-verse', 'official', 'staff', 'moderator', 'system', 'null', 'undefined',
  /* Shadowed by a route (`GET /halls/mine`); a Hall here would be silently unreachable by slug. */
  'mine',
]);

/** A slug is lowercase letters, digits and single hyphens — nothing that needs escaping in a URL. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Whether a slug is usable. Pure, so the rule is testable without a database. */
export function isUsableSlug(slug: string): boolean {
  if (slug.length < HALL_LIMITS.slugMinLength || slug.length > HALL_LIMITS.slugMaxLength) return false;
  if (!SLUG_PATTERN.test(slug)) return false;
  return !RESERVED_SLUGS.has(slug);
}


/**
 * WHAT A SCHEDULED STUDY MAY BE — the same move as a room's, for the same reason: the box a leader
 * titles one in was stating the ceiling on its own.
 */
export const SESSION_LIMITS = {
  titleMaxLength: 80,
  defaultDurationMinutes: 60,
  maxDurationMinutes: 240,
  /** A spam control on how many promises one reader may leave across the map. */
  maxScheduledPerUser: 5,
  /** How long after `startsAt` a session may still be opened before the sweep gives up on it. */
  graceMinutes: 30,
} as const;

/**
 * WHAT A ROOM MAY BE, in numbers — and the arithmetic that chose them.
 *
 * Here rather than in the server's schema because a client states some of these too: the box a
 * reader titles a room in had `maxLength={80}` written into it, and a box that allows more than the
 * server takes loses somebody's words on save. The cost reasoning travels with the numbers, which
 * is where it is most useful — every one of them is a bill.
 */
export const ROOM_LIMITS = {
  /** Concurrent publishers. 8 keeps a full room at ~43 GB/hour; the max keeps it under ~65. */
  defaultStageLimit: 8,
  maxStageLimit: 12,
  /** People present. Cost is linear in this and linear in the stage — the product is what bites. */
  defaultCapacity: 100,
  maxCapacity: 300,
  /** Video (COMMUNICATION.md §8) is only ever a small-room capability. */
  maxVideoStageLimit: 4,
  /**
   * AND ONLY A SMALL ROOM — which the stage cap alone does not make it.
   *
   * §8 permits video and says the caps are structural: `stage` policy and four publishers. Cost is
   * **publishers × subscribers**, so capping only the publishers capped one factor of a product: a
   * video room could still be created at `capacity: 300`, and 4 cameras at ~600 kbps sent to 300
   * people is ~7.2 Mbps × 300 ÷ 8 ≈ **324 GB/hour** — the exact failure §8 warns about ("it cannot
   * be turned on for three hundred people"), reached by the other axis.
   *
   * 30 comes from the arithmetic already in this file rather than from taste: the most expensive
   * AUDIO room this product permits is 300 present with 12 publishing at ~20 kbps, which is
   * 300 × 12 × 20 kbps = 72 Mbps. A video room is `capacity × 4 × 600 kbps`, and 30 × 4 × 600 kbps
   * is the same 72 Mbps. **No room may cost more per hour than the most expensive room already
   * allowed** — so video changes the shape of a conversation and not the bill.
   */
  maxVideoCapacity: 30,
  titleMaxLength: 80,
  /** How many rooms one reader may have open at once — a spam control, not a scarcity one. */
  maxOpenRoomsPerUser: 3,
  /*
   * How long a just-opened room is listed before anyone is counted in it.
   *
   * GROWTH.md §4: "a voice room with nobody in it is worse than no voice room at all — it is a
   * promise the product visibly fails to keep." So an EMPTY room is not listed. But occupancy is
   * webhook-fed, and between creating a room and the first event landing it reads zero — without
   * this window a reader's own new room would be invisible to everyone else for those seconds, and
   * no room could ever gather a second person.
   */
  newRoomGraceMs: 2 * 60 * 1000,
  /**
   * How long an empty room may sit before the sweep reclaims it.
   *
   * Generous on purpose. This is a BACKSTOP for a collapse whose webhook never arrived, and the
   * state it acts on — `occupancy: 0` — is itself webhook-fed, so during an outage a room full of
   * talking people looks empty. An hour is long enough that it never races the real path and short
   * enough that a reader is not left holding rooms they cannot see.
   */
  idleAfterMs: 60 * 60 * 1000,
} as const;

/**
 * WHAT A HALL MAY BE, in numbers — one statement, for the server that enforces them and the boxes a
 * leader types into.
 *
 * These lived in the server's schema, where no client could reach them, so every client wrote its
 * own copy: `maxLength={60}` on two name boxes, `{500}` on a description, `{32}` on an address,
 * `{300}` on a link. One of them said so out loud — *"Matches `HALL_LIMITS.maxLinks` on the server,
 * which refuses more"* — which is the clearest admission there is that a number has been copied and
 * nothing keeps the copies in step.
 *
 * WHICH WAY IT BREAKS DECIDES HOW BAD IT IS, and neither is loud. A box that allows more than the
 * server takes lets a leader write a description and lose it on save. A box that allows less
 * quietly refuses what the server would have accepted, and nobody can tell at all.
 *
 * The membership and co-host ceilings are here too, though no box enforces them: they are what a
 * client needs to SAY — "25 members on a free Hall" is a sentence, and a sentence with its own copy
 * of the number is the same defect wearing prose.
 */
export const HALL_LIMITS = {
  slugMinLength: 3,
  slugMaxLength: 32,
  nameMaxLength: 60,
  descriptionMaxLength: 500,
  maxLinks: 6,
  /** An outbound link's address, and the label a leader may put on it. */
  linkUrlMaxLength: 300,
  linkLabelMaxLength: 60,
  /** `HALLS.md §2` — the free ceiling, chosen so a free Hall runs a REAL group, not a demo one. */
  freeMemberLimit: 25,
  /** A hard ceiling under whatever any tier sells, the same belt-and-braces as a room's capacity. */
  maxMemberLimit: 1000,
  /** Co-hosts are a paid tier (`HALLS.md §3`); this is the ceiling, not the entitlement. */
  maxModerators: 20,
  /** How many Halls one reader may own — a spam control, not a scarcity one. */
  maxPerOwner: 3,
} as const;

/**
 * THE LONGEST A READER'S CHOSEN NAME MAY BE.
 *
 * It goes into the LiveKit token, every Hall roster, the pending queue, the ban list and the
 * moderation log — and in a product whose subject is where traditions disagree, a pseudonym is not
 * a preference but the difference between arguing in public and being findable for it.
 *
 * Sixty characters, and deliberately the SAME as a Hall's name: the two appear in the same rows, so
 * a row built for one has to hold the other. That reason was written on the server's copy and the
 * tie it describes was held by nothing — two constants, in two files a client could not read, both
 * transcribed into four boxes.
 */
export const DISPLAY_NAME_MAX = HALL_LIMITS.nameMaxLength;


/**
 * AND THE CEILING, which was two numbers for one door.
 *
 * The floor above is shared, and the comment beside the study's own copy of it says so in as many
 * words — *"Same floor as a room's, and the same reason"*. The ceiling was not: a room took 200
 * characters, a study took 128, and re-keying a study took 128. Same concept, three statements, two
 * numbers, and no reason written for the difference anywhere. A leader who set a long passphrase on
 * a room and tried the same one on their weekly study was refused by a rule the product never
 * explained, because there was nothing to explain — one of the two was simply typed first.
 *
 * There IS a reason for having a ceiling at all, and it is the one written on `JoinRoomDto`: a
 * passphrase is compared against a stored secret with scrypt, so an unbounded string lets a caller
 * push arbitrary work into an endpoint anyone signed in can reach. 200 bounds that as well as 128
 * does, and it is the number that was already in the door — so nothing a reader has set stops
 * working, which the other direction could not promise.
 */
export const MAX_PASSPHRASE = 200;


/**
 * THE LONGEST A POST MAY BE — one number, in six places until this existed.
 *
 * `MIN_PASSPHRASE` above is here because *"a minimum length is exactly the kind of constant that
 * gets copied once and then updated once"*, and this is that sentence again with a bigger number:
 * 4000 was written out in both post DTOs on the server, in both boxes of the web's discussion, and
 * in both boxes of the phone's. Six statements, three workspaces, and nothing holding any of them
 * against another.
 *
 * WHICH WAY IT BREAKS DECIDES HOW BAD IT IS, and neither is loud. Lower the server's and a reader
 * types happily to the box's limit and is refused on send — the whole essay, and no way to tell
 * which part was too much. Lower a client's and the product quietly refuses what the server would
 * have taken, which nobody can see at all.
 *
 * It is deliberately far above `MAX_CHAT_MESSAGE`, and the two are a pair: a room's chat carries a
 * question, and *a pasted essay belongs in the discussion, which is durable* (§8). Two limits
 * because they are two products, each stated once.
 */
export const MAX_POST_BODY = 4000;


export function roomToOpen(fields: RoomFields): NewRoom | null {
  const title = fields.title.trim();
  if (!title) return null;

  const passphrase = fields.access === 'passphrase' ? (fields.passphrase ?? '').trim() : undefined;
  if (fields.access === 'passphrase' && (passphrase ?? '').length < MIN_PASSPHRASE) return null;

  return {
    title,
    hallId: fields.hallId || undefined,
    access: fields.access,
    passphrase,
  };
}

/**
 * MAY THIS READER CALL OFF THIS SESSION?
 *
 * The server's filter is `{ _id, ownerId, status: 'scheduled' }` — the authority is in the query,
 * as everywhere in this plane — and this is that same sentence on the client side, so what is
 * OFFERED and what is HONOURED cannot drift apart.
 *
 * Both halves matter. Only the owner: a study is somebody's promise, and calling off another
 * leader's is not moderation, it is impersonation. Only while `scheduled`: a session that is LIVE
 * is a room with people in it, and the control for that is closing the room, which says what it
 * does. A cancelled or ended one is already over.
 */
export function mayCancelSession(session: SessionView, meId: string | null): boolean {
  return meId !== null && session.ownerId === meId && session.status === 'scheduled';
}

/**
 * Whether this reader may give a study a NEW WORD — the server's own filter, written client-side.
 *
 * Three conditions, and each is in that filter for a reason: their own study, because a promise
 * belongs to whoever made it; one already held behind a word, because rotating is not a way to lock
 * a study a community was told anyone could attend; and one still to come or under way, because
 * re-keying a study that has ended or been called off changes nothing anybody can use.
 *
 * A LIVE study counts, unlike cancelling: a leader who discovers the leak twenty minutes in needs
 * tonight's door changed and next Thursday's with it.
 */
export function mayRekeyStudy(session: SessionView, meId: string | null): boolean {
  return meId !== null
    && session.ownerId === meId
    && session.access === 'passphrase'
    && (session.status === 'scheduled' || session.status === 'live');
}

/**
 * Whether a room has no more space, as far as the count everyone can see knows.
 *
 * The SFU enforces capacity and is the truth; this is the same fact a moment earlier, so a reader
 * is not offered a door that cannot open. Until it existed the sequence was: tap a full room, get a
 * token, be refused by the room server, and read *"something went wrong"* — the most ordinary thing
 * that can happen to a popular session, reported as a broken product.
 *
 * Shared rather than written in each renderer, because the web and the phone disagreeing about
 * whether a room is joinable is a bug that only shows up on one of them.
 */
export function roomIsFull(room: RoomView): boolean {
  return room.occupancy >= room.capacity;
}

/**
 * Somebody a room has barred, as its own leadership sees them.
 *
 * `displayName` is null for a reader who has since deleted their account — a placeholder would be a
 * small lie about a person, and the row still has to work, because clearing a ghost off the list is
 * exactly the sort of tidying a host does.
 */
export interface BannedReaderView {
  userId: string;
  displayName: string | null;
}

/** As much of a person in a room as deciding whether to draw their row needs. */
export interface RosterMember {
  identity: string;
  /** Making sound right now. The one property that decides who a host is looking for. */
  speaking: boolean;
  /** On the stage, or in an open room. A listener is the audience. */
  canSpeak: boolean;
}

/** Who to draw, and how many are left over. `rows` keeps the order it was given. */
export interface RosterView<T> {
  rows: T[];
  hidden: number;
}

/**
 * How many rows a roster draws before it starts leaving people out.
 *
 * Not a round number for its own sake: `ROOM_LIMITS.maxStageLimit` is 12, so this is the whole
 * stage twice over. A room at the design's reference load holds 300 with 8 speaking, and the 280
 * listeners below them are an audience rather than a list anybody reads.
 */
export const ROSTER_ROWS = 25;

/**
 * How much of a raised-hand queue is worth drawing.
 *
 * A QUEUE IS NOT A ROSTER, and the difference decides the shape of both. Order here IS the fairness
 * rule — `$addToSet` means asking first is being asked first — so the front of it is the part a
 * host acts on, and a prefix is the honest cut. The roster has no order worth keeping and so is
 * filtered instead. D22's own example is a leader with 300 listeners; if a third of them ask at
 * once, a host works the front of that queue and reads a number for the rest.
 */
export const QUEUE_ROWS = 10;

/**
 * WHO TO DRAW IN A ROOM OF THREE HUNDRED.
 *
 * `ROOM_LIMITS.maxCapacity` is 300 and `§2`'s whole cost argument is priced on rooms that size, so
 * a roster that renders every person is the normal case at the load this layer was designed for —
 * and it fails there in two ways at once. The cheap one is 300 rows of four buttons re-rendering
 * every time the active speaker changes. The one that matters is that **rule 2's controls become
 * unreachable**: a host who can hear somebody shouting has to scroll a list of hundreds to find
 * the row that is shouting, while the shouting continues.
 *
 * So: whoever is SPEAKING is always drawn, however far down the room they joined. Then the rest in
 * the order they came, up to `rows`. The remainder is a number the reader can open.
 *
 * ORDER IS NEVER CHANGED, and that is the whole reason this returns a filtered list rather than a
 * sorted one. Sorting the speakers to the top would move every row underneath them each time
 * somebody starts or stops talking — under the cursor of the host who is reaching for "remove", in
 * the one situation where they are in a hurry. A list that reorders itself while being clicked is
 * how a moderator removes the wrong person.
 *
 * `self` is always kept for the same reason a mirror is: a reader scrolling for their own name and
 * not finding it has no way to tell whether they are in the room.
 */
export function rosterFor<T extends RosterMember>(
  members: readonly T[],
  options: { rows?: number; revealAll?: boolean; selfId?: string | null } = {},
): RosterView<T> {
  const budget = Math.max(options.rows ?? ROSTER_ROWS, 1);
  if (options.revealAll || members.length <= budget) return { rows: [...members], hidden: 0 };

  /* Kept whatever the budget says: the budget is a floor under who a host needs to reach, never a
     ceiling on it. If more people are speaking than there is room for, they are all still drawn. */
  const must = new Set<string>();
  for (const member of members) {
    if (member.speaking || member.identity === options.selfId) must.add(member.identity);
  }

  /* The stage before the audience — a speaker who happens to be silent this second is still one of
     the few people in the room a host may need to mute, and there are at most `maxStageLimit`. */
  let room = Math.max(budget - must.size, 0);
  for (const member of members) {
    if (room === 0) break;
    if (!must.has(member.identity) && member.canSpeak) { must.add(member.identity); room -= 1; }
  }
  for (const member of members) {
    if (room === 0) break;
    if (!must.has(member.identity)) { must.add(member.identity); room -= 1; }
  }

  const rows = members.filter(member => must.has(member.identity));
  return { rows, hidden: members.length - rows.length };
}

/**
 * A reported post, as the moderator who has to judge it sees it.
 *
 * The post plus a NAME: nobody can tell a first offence from a tenth by reading 24 hexadecimal
 * characters. Null where the account has since been deleted.
 */
export interface ReportedPostView extends PostView {
  author: string | null;
  /**
   * HOW MANY PEOPLE OBJECTED — and only here.
   *
   * It used to be on the ordinary `PostView`, so every reader of every discussion was told how many
   * people had objected to a post no moderator had judged yet. No client ever drew it, and the fact
   * itself is precisely what the moderation queue is kept private FOR: that queue is a list of
   * accusations about people, and publishing the same fact one post at a time is the same
   * disclosure in smaller print — inviting the pile-on it exists to help somebody stop.
   */
  reportCount: number;
}

/**
 * A suspended account, as the moderator who can lift it sees them.
 *
 * The list exists because nothing else can find them: their posts may be gone, and they are in no
 * room to be seen in. Without it, undoing a suspension means editing the database — which is the
 * state the control replaced.
 */
export interface SuspendedReaderView {
  id: string;
  displayName: string;
}

/**
 * One act by a site moderator, as another site moderator reads it.
 *
 * The two acts the site itself can take on a reader: suspending an account and lifting it. Names
 * rather than ids, `null` for somebody who has since erased their account — the row survives them,
 * because a moderator's record must not be erasable by the person they moderated deleting theirs.
 */
export interface SiteLogEntry {
  id: string;
  action: 'readerSuspended' | 'readerReinstated';
  actor: string | null;
  subject: string | null;
  /** ISO-8601, rendered in the reader's own zone like every other time in this product. */
  at: string;
}

/** What a reader may be offered on somebody's post. */
export type PostVerb = 'edit' | 'remove' | 'report';

/**
 * WHICH VERBS TO OFFER ON A POST.
 *
 * Three branches, and each one is a decision rather than a formality:
 *
 * - **Signed out, nothing.** All three need an account, and offering a control that will answer
 *   401 is worse than offering none — it spends a tap to produce a sign-in prompt the reader did
 *   not ask for, in place of the thing they pressed.
 * - **Mine: edit and remove.** Not report — reporting your own words is noise in the queue a
 *   moderator reads, and the remove button is the honest version of that impulse.
 * - **Someone else's: report.** Never edit or remove; those are the author's and a moderator's,
 *   and a moderator acts through the queue rather than through this row.
 *
 * `editedAt` is deliberately NOT consulted, and neither is any window — **because there is no
 * window**. This paragraph used to say there was one and that the server owned it; the server
 * owns no such number, and a comment describing a mechanism nobody wrote is worse than no
 * comment, because the next reader takes it for a description of the product. If a window is
 * ever added it will be the server's, and the reason for not copying it here stands: two
 * copies of a number diverge.
 *
 * It decides only what is OFFERED — every call is checked again server-side.
 */
export function postVerbs(post: PostView, meId: string | null): PostVerb[] {
  if (meId === null) return [];
  return post.authorId === meId ? ['edit', 'remove'] : ['report'];
}

/**
 * What a reader may ask for when opening a room. Every number is a request, not a demand — the
 * server clamps rather than refuses, so an ambitious capacity produces a working room.
 */
export interface NewRoom {
  /** Optional — an unnamed room shows the node's name instead. */
  title?: string;
  /**
   * Open it under a Hall: the room appears on that Hall's page and its leadership moderate it.
   * The server refuses this unless the caller leads the Hall or is an active member of it.
   */
  hallId?: string;
  access?: RoomAccess;
  /** Required when `access` is 'passphrase'; sent once, hashed by the server, never stored here. */
  passphrase?: string;
  policy?: RoomPolicy;
  stageLimit?: number;
  capacity?: number;
  video?: boolean;
}

export interface SocialClientOptions {
  /** The API server origin. `''` is same-origin (web only; a phone always has an absolute base). */
  apiBase: string;
  fetch: SocialFetch;
  authorize: Authorize;
}

const PREFIX = 'api/v1';

function join(base: string, path: string): string {
  const root = base.replace(/\/+$/, '');
  return `${root}/${PREFIX}/${path}`;
}

/** Thrown when a write needs a signed-in reader and there is none. */
export class SocialAuthError extends Error {
  constructor() {
    super('sign in to take part');
    this.name = 'SocialAuthError';
  }
}

/**
 * Thrown when a signed-in reader may not join a room — banned, or the room has closed.
 *
 * Distinct from SocialAuthError on purpose: offering "sign in to join" to someone who IS signed in
 * and barred is confusing and useless, and the two need different words on the screen.
 */
export class RoomClosedToYouError extends Error {
  constructor() {
    super('this room is not open to you');
    this.name = 'RoomClosedToYouError';
  }
}

/**
 * THE MICROPHONE WAS ASKED FOR AND NOTHING WAS PUBLISHED.
 *
 * Distinct from "you may not speak here", which is a grant and is answered by the control being
 * unavailable. This is the case where the reader HAS the floor, the button is live, the room server
 * takes the request and no track appears — measured at about one press in three in the seconds
 * after somebody is given the floor, and until now the press simply did nothing and said nothing.
 *
 * A reader pressing a live button and getting silence has no way to tell that from a product that
 * is broken, and the thing they will do is press it again — which is what the browser gate had to
 * do to get past it.
 */
export class MicrophoneDidNotOpenError extends Error {
  constructor() {
    super('your microphone did not open');
    this.name = 'MicrophoneDidNotOpenError';
  }
}

/** Thrown when a moderation control is used by someone who does not host that room. */
export class NotAHostError extends Error {
  constructor() {
    super('only a host can do that');
    this.name = 'NotAHostError';
  }
}

/**
 * THE SERVER ANSWERED, AND REFUSED, AND SAID WHY — and the why is fit to put on a screen.
 *
 * This exists so a renderer can tell that apart from *we never got an answer*. A failed fetch also
 * arrives as an `Error`, and `Failed to fetch` is not a sentence to show anybody; a UI that renders
 * every `Error.message` would put it there. So the refusals the server writes deliberately get
 * their own type, and everything else keeps whatever the screen already did with it.
 */
/**
 * A REFUSAL WITH A NAME, so a reader can be told about it in their own language.
 *
 * The server writes its refusals in English, and until now the client put that English on the
 * screen — every one of them, in a product that reads in three languages. A Hebrew reader typing a
 * passphrase into a room got "that passphrase does not open this room", mid-page, in a page that is
 * otherwise entirely Hebrew, and the same for a leader whose chosen Hall address was already taken.
 *
 * The fix is not to translate on the server: the server does not know who is reading, and a
 * refusal is not the server's sentence to write in somebody else's language. So a refusal now
 * carries a NAME as well as its sentence, and each client says it in its own words. The English
 * sentence stays, and stays authoritative for anything unnamed — a refusal nobody has named yet
 * still reads in English rather than as a blank, which is the same bargain the phrase dictionary
 * makes.
 *
 * Named here rather than on the server because BOTH sides need the list and only one can own it —
 * exactly the reasoning behind `ACCOUNT_SUSPENDED`, of which this is the generalisation.
 */
/*
 * A DOOR CANNOT BE NAMED HERE, and the attempt is worth recording. "that passphrase does not open
 * this room" and "this room is by invitation" were the first two names on this list and both were
 * dead: the join answers 403 for banned, closed, wrong-passphrase and invite-only alike, on
 * purpose, so that a refusal cannot be used as an oracle — and this client turns every one of them
 * into `RoomClosedToYouError` before a body is ever read. The panel already says that in the
 * reader's language. A name nothing can reach is the defect this layer produces most often; adding
 * one while fixing a different instance of it is the joke that writes itself.
 */
export const REFUSAL_REASONS = [
  /* The reader, not the request — the one that already had a constant of its own. */
  'suspended',
  /* A limit. Ordinary: the room filled up between the list being drawn and the door being tried. */
  'roomFull',
  'stageFull',
  /* Pressing faster than the server will answer — written by this client, not by any server. */
  'tooFast',
  /* A leader naming their Hall, which is the first thing a community leader ever does here. */
  'addressTaken',
  /*
   * THE LIMITS, which are not failures — they are the product working, met by the readers who use
   * it most. Each carries the number the server actually counted rather than the ceiling it
   * compared against, so a count that slipped past a race is reported honestly.
   */
  'tooManyRooms',
  'tooManyHalls',
  'tooManyCoHosts',
  /*
   * AND THE THREE THE GROUP ABOVE DESCRIBED AND DID NOT INCLUDE.
   *
   * Each was bare English with the number written INTO the sentence — `this hall holds 25 members`,
   * `remove one of your 3 channels`, `cancel one of your 5 scheduled sessions` — which is the two
   * things this vocabulary exists to stop, in one line each. A Hebrew-reading leader whose community
   * filled up read all three in English, and the paragraph above already says why the number cannot
   * live in the prose: *"'this room is full (8)' and its Hebrew do not agree on where the 8 goes"*.
   *
   * They are the same kind of thing as their neighbours and were found by asking which refusals a
   * reader can meet are unnamed — 85 of 102, though nearly all of those are mapped by STATUS in the
   * client and never reach a screen. These four do.
   */
  'hallIsFull',
  'tooManyChannels',
  'tooManySessions',
  /*
   * AND "THAT IS NOT A ___" — met by typing something the server will not take, which is an
   * ordinary thing to do and the moment a reader most needs to be told what would work.
   */
  'notAName',
  /*
   * Carries the url, unlike `notAName` beside it, because a Hall has SEVERAL links and a leader
   * told "that is not a link" about a list of four has been told almost nothing.
   */
  'notALink',
  'addressNotUsable',
  'notADiscordWebhook',
  'notAYouTubeChannel',
  /*
   * THE POST IS NOT THERE — written by this client rather than read from a server, and named
   * for what the 404 actually means.
   *
   * It used to say **"that post is no longer yours to edit"**, and four comments around it
   * described the *window the server allows*. There is no window: `PostsService.edit` is
   * `findOneAndUpdate({ _id, authorId })` and an author may correct their own words for ever.
   * So the sentence invented a time limit that does not exist, and hid the thing that had
   * actually happened — the post was removed, by a moderator or by this reader on another
   * device — which is the only way a client that offers the control can reach this at all
   * (`postVerbs` never offers edit or remove on somebody else's post).
   *
   * One name for both verbs, because it is one fact. The server's own English stays precise
   * about the lookup ("no such post of yours to edit"); this is what a reader is told.
   *
   * WRITTEN BY THIS CLIENT, like `tooFast` above and unlike everything else here: the server
   * answers 404 and the NAME is put on it in `editPost` and `removePost`. Said out loud because a
   * sweep asking which of these the server never emits finds this one and reads it as a dead
   * phrase — it is not, and the two that share that property should be findable together.
   */
  'postIsGone',
  /*
   * AND THE BOX BEING AWAY (D16), which is not exotic: every room is on one self-hosted SFU, and
   * that is the cost decision the whole layer rests on. The sentences say which half of an act
   * landed, because a host who thinks a silencing worked and a reader who thinks their room is
   * gone both do the wrong next thing.
   */
  'voiceUnreachable',
  'silencedButNotMuted',
  'nobodyMuted',
  /*
   * AND THE TWO ACTS WHOSE DOCUMENT HALF LANDED WHILE THEIR LIVE HALF DID NOT.
   *
   * `nobodyMuted` says nothing happened, which is the whole of the truth there: muting one person
   * is entirely an act on the SFU. These two are not that. Ejecting somebody writes a ban that
   * holds at the token mint, and taking somebody off the stage writes the room document that
   * decides every future join — so the record IS changed and only the live connection is untouched.
   *
   * Two sentences rather than one, because what the host should do next differs: after a failed
   * ejection the person is still in the room and the answer is to try again; after a failed stage
   * change nothing is on fire and the grant will be right the moment they reconnect.
   */
  'stillInTheRoom',
  'stageNotLive',
] as const;

/** One of the refusals a reader meets often enough to be worth saying in their own language. */
export type RefusalReason = (typeof REFUSAL_REASONS)[number];

const isRefusalReason = (value: unknown): value is RefusalReason =>
  typeof value === 'string' && (REFUSAL_REASONS as readonly string[]).includes(value);

/** The numbers a refusal's sentence needs — "this room is full (8)" is a phrase and a capacity. */
export type RefusalValues = Readonly<Record<string, string | number>>;

/*
 * Values come off the wire, so every one is checked rather than trusted. A body that carries
 * something else in `values` — an object, a nested array, a null — loses that entry rather than
 * putting `[object Object]` into a sentence on somebody's screen.
 */
const readValues = (raw: unknown): RefusalValues => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const values: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'string' || typeof value === 'number') values[key] = value;
  }
  return values;
};

/**
 * THE SERVER ANSWERED, AND REFUSED, AND SAID WHY — and the why is fit to put on a screen.
 *
 * This exists so a renderer can tell that apart from *we never got an answer*. A failed fetch also
 * arrives as an `Error`, and `Failed to fetch` is not a sentence to show anybody; a UI that renders
 * every `Error.message` would put it there. So the refusals the server writes deliberately get
 * their own type, and everything else keeps whatever the screen already did with it.
 *
 * `reason` is that refusal's name when it has one and `null` when it does not, and `message` is
 * always a sentence — so a renderer that knows the name can say it in the reader's language, and
 * one that does not still has something true to show.
 */
export class RefusedError extends Error {
  readonly reason: RefusalReason | null;

  readonly values: RefusalValues;

  constructor(message: string, reason: RefusalReason | null = null, values: RefusalValues = {}) {
    super(message);
    this.name = 'RefusedError';
    this.reason = reason;
    this.values = values;
  }
}

/**
 * THE PHRASE KEY FOR A NAMED REFUSAL, or null when the server did not name it.
 *
 * Two apps render these — a browser and a phone — each with a dictionary of its own, and neither is
 * the place to decide what a reason is called. Deciding it once here also puts the pairing in the
 * TYPE: a renderer's `t` must accept every key this can return, so a reason added above with no
 * phrase in a dictionary stops THAT app compiling, naming the reason it has no words for. A test
 * would have to be written per app and could only be written by somebody who remembered to.
 */
export const refusalKey = (refused: RefusedError): `refusal.${RefusalReason}` | null =>
  (refused.reason === null ? null : `refusal.${refused.reason}`);

/**
 * WHAT THE SERVER SAYS TO A SUSPENDED READER — one sentence, written once.
 *
 * The server throws it and this client recognises it, so the two cannot drift into saying different
 * things about the same state. It is compared rather than parsed: an exact match on a constant both
 * sides import, not a phrase either of them can quietly reword.
 */
export const ACCOUNT_SUSPENDED = 'this account has been suspended';

/**
 * THE READER IS SIGNED IN AND THE ANSWER IS STILL NO — which nothing could say.
 *
 * A suspended account used to answer **401** on every request, so every panel in every client did
 * the only sensible thing with a 401 and offered to sign in. Pressing it reached Google, came back,
 * and was refused at the callback: a loop with no way out and no explanation anywhere inside the
 * product. It is the same mistake this layer already fixed for rooms — *"telling a banned reader to
 * sign in is confusing and useless"* — arriving one layer further in, where the ban is the site's
 * own and the heaviest act it has.
 *
 * A `RefusedError`, deliberately, and NOT a `SocialAuthError`: every panel already checks for the
 * auth one first and offers sign-in, then falls through to showing the server's own sentence. So
 * being the second kind is what makes nine screens tell the truth without nine edits.
 */
export class SuspendedError extends RefusedError {
  constructor() {
    super(ACCOUNT_SUSPENDED, 'suspended');
    this.name = 'SuspendedError';
  }
}

/**
 * THE QUERY FOR A PAGE FURTHER BACK — built once, because both logs page the same way and a rule
 * written twice is a rule that differs in one of them.
 *
 * The id is only sent beside a time: alone it names a row with no place in the order, which is
 * not a position a server can walk back from.
 */
function walkingBackFrom(cursor: { before?: string; beforeId?: string }): string {
  if (!cursor.before) return '';
  const at = `?before=${encodeURIComponent(cursor.before)}`;
  return cursor.beforeId ? `${at}&beforeId=${encodeURIComponent(cursor.beforeId)}` : at;
}

/**
 * THE QUERY FOR THE NEXT PAGE OF A LIST READ FORWARDS — a roster, and the queue at a door.
 *
 * `walkingBackFrom` above is for a record, where the question is what happened BEFORE this. A
 * roster is read oldest first, because it is a history of who arrived, and a queue is read that
 * way because its order is its fairness rule — so these move the other way.
 *
 * The id travels only beside a time, for the same reason: alone it names a row with no place in
 * the order, which is not a position a server can resume from.
 */
function goingOnFrom(cursor: { after?: string; afterId?: string }): string {
  if (!cursor.after) return '';
  const at = `?after=${encodeURIComponent(cursor.after)}`;
  return cursor.afterId ? `${at}&afterId=${encodeURIComponent(cursor.afterId)}` : at;
}

export class SocialClient {
  private readonly apiBase: string;
  private readonly doFetch: SocialFetch;
  private readonly authorize: Authorize;

  constructor(options: SocialClientOptions) {
    this.apiBase = options.apiBase;
    this.doFetch = options.fetch;
    this.authorize = options.authorize;
  }

  /** A node's discussion — anonymous, newest first. */
  /**
   * A NODE'S DISCUSSION, newest first — and everything before what is already held.
   *
   * The server has taken a `before` cursor since the discussion was built, and this took no options
   * at all: every client asked for the newest fifty and **nothing could reach the fifty-first**. On
   * a busy passage that is the older half of the argument gone — in the plane whose entire subject
   * is showing where traditions disagree, and to the author of the post as much as to anybody else.
   *
   * The same cursor as the Hall's log, and now literally so — `walkingBackFrom` builds all three.
   * A cursor rather than a page number because the list grows at the newest end while somebody is
   * reading: `skip: 50` re-reads rows that have shifted down, showing one post twice and missing
   * another. And it names the ROW rather than the moment, because two posts can share a
   * millisecond — ordinary here, where the writers are every reader at once — and a page boundary
   * between them left the second unreachable by anything, ever.
   */
  async listPosts(nodeId: string, options: { before?: string; beforeId?: string } = {}): Promise<PostView[]> {
    const query = walkingBackFrom(options);
    const response = await this.doFetch(join(this.apiBase, `nodes/${encodeURIComponent(nodeId)}/posts${query}`));
    if (!response.ok) throw new Error(`could not load the discussion (${response.status})`);
    return await response.json() as PostView[];
  }

  /** Add a post — signed-in; `authorize` attaches the identity. */
  async addPost(nodeId: string, body: string): Promise<PostView> {
    const response = await this.doFetch(join(this.apiBase, `nodes/${encodeURIComponent(nodeId)}/posts`), this.authorize({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ body }),
    }));
    if (response.status === 401) throw new SocialAuthError();
    /* Posting is throttled, and "you are going too fast" is something a reader can act on where
       "could not post (429)" is not. */
    if (!response.ok) throw await this.refused(response, 'could not post');
    return await response.json() as PostView;
  }

  /**
   * Correct your own words. There is no window — an author may edit what they wrote for as long
   * as it is there, which is what `PostsService.edit` does and all it does.
   *
   * Answered by the server since the discussion was built and called by no client — found only
   * after the route sweep was tightened, because matching on path SEGMENTS counted `posts/:id` as
   * reached on the strength of `nodes/:id/posts` appearing elsewhere.
   */
  async editPost(postId: string, body: string): Promise<PostView> {
    const response = await this.doFetch(join(this.apiBase, `posts/${encodeURIComponent(postId)}`), this.authorize({
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ body }),
    }));
    if (response.status === 401) throw new SocialAuthError();
    /*
     * A DELIBERATE OVERRIDE, and it has to be a `RefusedError` to survive: a panel that shows only
     * what the server "deliberately wrote" cannot otherwise tell a considered client sentence from
     * a dropped connection, and it showed the generic line for both.
     *
     * The server says "no such post OF YOURS to edit", which is exactly the lookup it did. What
     * that means to somebody who was just offered an Edit button is that the post has gone — a
     * moderator took it down, or they removed it themselves somewhere else. The old sentence said
     * "no longer YOURS to edit" and invented a time limit; see `postIsGone` for why there is none.
     * Deliberately not `NotAHostError`, which is about rooms and would read as a lie here.
     */
    if (response.status === 404) throw new RefusedError('that post is no longer there', 'postIsGone');
    if (!response.ok) throw await this.refused(response, 'could not edit that post');
    return await response.json() as PostView;
  }

  /** Take back something you said. A moderator may also remove somebody else's. */
  async deletePost(postId: string): Promise<void> {
    const response = await this.doFetch(join(this.apiBase, `posts/${encodeURIComponent(postId)}`), this.authorize({
      method: 'DELETE',
    }));
    if (response.status === 401) throw new SocialAuthError();
    /*
     * THE SAME SITUATION AS `editPost`'S 404, AND IT HAD THE OPPOSITE ANSWER. That method says, in
     * a comment eight lines up, *"Deliberately not `NotAHostError`, which is about rooms and would
     * read as a lie here"* — and this one threw exactly that. Neither discussion panel handles it
     * either, so it fell to the generic line: a distinction computed and then discarded.
     *
     * AND IT WAS STILL HALF-ANSWERED: it got the class and not the NAME, so it read from no
     * dictionary and a Hebrew reader met an English sentence in the middle of a Hebrew page —
     * which is the whole reason `RefusalReason` exists. Same name as the edit, because it is the
     * same fact: the post is not there. A reader who asked to remove it has got what they wanted.
     */
    if (response.status === 404) throw new RefusedError('that post is no longer there', 'postIsGone');
    if (!response.ok) throw await this.refused(response, 'could not remove that post');
  }

  /**
   * Report a post.
   *
   * `COMMUNICATION.md §9` names reporting as part of the moderation floor this product launches
   * with — "rate-limit, report, mute, stage, kick, ban" — and it was the one of those a reader had
   * no way to do. For a product whose subject is where traditions disagree, the absence of a way to
   * say "this is not all right" is the gap that matters most of the six.
   */
  async reportPost(postId: string): Promise<void> {
    const response = await this.doFetch(join(this.apiBase, `posts/${encodeURIComponent(postId)}/report`), this.authorize({
      method: 'POST',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not report that post');
  }

  /**
   * WHAT HAS BEEN REPORTED — site moderators only, and the read that made reporting mean something.
   *
   * Reporting incremented a counter nothing ever read back: a reader tapped Report, was told
   * "Reported", and no route, page or client could show it to anybody who could act. Removing has
   * always accepted a moderator, so the power existed and the queue that would point it at
   * something did not.
   *
   * `waiting` is the whole queue and `posts` is the page of it — a capped read that says what it
   * left out, rather than one that looks like everything.
   */
  async reportedPosts(): Promise<{ waiting: number; posts: ReportedPostView[] }> {
    const response = await this.doFetch(join(this.apiBase, 'moderation/posts'), this.authorize({}));
    if (response.status === 401) throw new SocialAuthError();
    /*
     * EMPTY FOR ANYBODY WHO MAY NOT ASK — a 403 — so the section simply is not there. That is a
     * real answer: an ordinary reader has no queue.
     *
     * IT USED TO BE THE ANSWER FOR EVERY FAILURE TOO, which §0 calls the sharpest form of this
     * defect: a moderator whose read failed got no queue, no count, and nothing saying why, so
     * the site looked quiet. The SCREEN was taught to tell the two apart; this is the half below
     * it that made that impossible to reach for anything the server itself reported.
     */
    if (response.status === 403) return { waiting: 0, posts: [] };
    if (!response.ok) throw new Error('could not read the moderation queue (' + response.status + ')');
    return await response.json() as { waiting: number; posts: ReportedPostView[] };
  }

  /**
   * GET THIS PERSON OUT OF HERE NOW — site moderators only.
   *
   * `isActive` is re-read on every request, so this ends their session on the next one, and it
   * disconnects them from whatever they are in, because the next request is not soon enough for
   * live audio. It lived in the database and nowhere else until now: a field an operator edited by
   * hand, which is a runbook rather than a control.
   */
  async suspendReader(userId: string): Promise<void> {
    const response = await this.doFetch(join(this.apiBase, `moderation/readers/${encodeURIComponent(userId)}/suspend`), this.authorize({
      method: 'POST',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not suspend that account');
  }

  /** Let them back in. An emergency control with no way back is one nobody dares use. */
  async reinstateReader(userId: string): Promise<void> {
    const response = await this.doFetch(join(this.apiBase, `moderation/readers/${encodeURIComponent(userId)}/suspend`), this.authorize({
      method: 'DELETE',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not reinstate that account');
  }

  /**
   * WHAT SITE MODERATORS HAVE DONE — suspensions and reinstatements, newest first.
   *
   * The suspended list answers "who is locked out right now"; it cannot say who locked them out,
   * when, or how many times this reader has been through it — which is what a second moderator
   * needs before deciding whether to lift it. Site moderators only, the same rule as a Hall's log:
   * the people who may read it are the people who already hold the power it records.
   *
   * An empty list rather than a throw when the server refuses: this sits beside the reports queue,
   * and a panel that vanishes because one of its four reads 403'd is worse than one section short.
   */
  /**
   * WHERE THE READER STOPPED — the row, not the moment.
   *
   * `before` alone was the cursor and a millisecond is not a unique key: two acts can share one,
   * and a page boundary falling between them asked for everything strictly before the second's own
   * timestamp — so that row was never returned by anything again. A record that cannot produce a
   * row is not a record, and this one exists so a disputed ejection is resolvable (D22).
   *
   * `beforeId` is the `id` every row already carries. Sent together or not at all.
   */
  async siteLog(options: { before?: string; beforeId?: string } = {}): Promise<SiteLogEntry[]> {
    const query = walkingBackFrom(options);
    const response = await this.doFetch(join(this.apiBase, `moderation/log${query}`), this.authorize({}));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw new Error('could not read what the moderators have done (' + response.status + ')');
    return await response.json() as SiteLogEntry[];
  }

  /** Who is suspended — the read that makes it reversible. Nothing else can find them. */
  /**
   * A page at a time, walked back by the ROW — and that is what makes the switch reversible.
   *
   * It read one capped list and stopped. The reinstate control is drawn on a row of it, so a
   * reader past the cap could not be reinstated through the product by anybody, which is exactly
   * the state this read was written to end.
   *
   * The row and not a moment: the user document records that an account is off and never when it
   * was turned off, so there is no instant to page by.
   */
  async suspendedReaders(options: { beforeId?: string } = {}): Promise<SuspendedReaderView[]> {
    const query = options.beforeId ? `?beforeId=${encodeURIComponent(options.beforeId)}` : '';
    const response = await this.doFetch(join(this.apiBase, `moderation/readers/suspended${query}`), this.authorize({}));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw new Error('could not read who is suspended (' + response.status + ')');
    return await response.json() as SuspendedReaderView[];
  }

  /** Looked at, nothing wrong with it — the other half of a queue. Site moderators only. */
  async clearReports(postId: string): Promise<void> {
    const response = await this.doFetch(join(this.apiBase, `posts/${encodeURIComponent(postId)}/reports`), this.authorize({
      method: 'DELETE',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not clear those reports');
  }

  /**
   * Who the reader is, or null when signed out.
   *
   * `id` is the same id posts, room hosts and voice participants are keyed by, so it is what lets
   * the UI answer "is that me?" — which the host controls need before they render.
   */
  async me(): Promise<Me | null> {
    const response = await this.doFetch(join(this.apiBase, 'auth/me'), this.authorize({}));
    /*
     * NOT SIGNED IN, AND COULD NOT ASK, ARE DIFFERENT ANSWERS — and this returned the first for
     * both. `if (!response.ok) return null` meant a 500, a proxy's 502, a 503 mid-deploy and a
     * genuine 401 all said the same thing: **nobody is here**.
     *
     * It is the recurring defect of this layer at the one place it costs the most. Identity is what
     * every other panel keys on, so one transient failure took a signed-in reader's account
     * controls, their own open rooms, the moderation queue, the Hall chooser and the ability to
     * post — and told them nothing, because "signed out" is not an error, it is a state.
     *
     * 401 and 403 are the answers that MEAN it: the session is absent, expired or refused, and null
     * is true. Anything else is a failure to ask, and it travels — so a caller can keep whatever it
     * already knew and say so, rather than concluding the reader is a stranger.
     */
    if (response.status === 401 || response.status === 403) return null;
    if (!response.ok) throw new Error(`could not read who is signed in (${response.status})`);
    return await response.json() as Me;
  }

  /**
   * WHAT THIS READER IS CALLED HERE — §5.1's promise, which had nothing behind it.
   *
   * The name arrived from Google at first sign-in and could not be changed, so a legal name went
   * into the voice token every room reads, into every Hall roster, the pending queue, the ban list
   * and the moderation log. In a product about where traditions disagree, a pseudonym is not a
   * preference — it is the difference between arguing in public and being findable for it.
   *
   * Their own only: the server takes no subject, so this cannot become a way to rename anybody else.
   */
  async renameMe(displayName: string): Promise<Me> {
    const response = await this.doFetch(join(this.apiBase, 'auth/me'), this.authorize({
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ displayName }),
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not change your name');
    return await response.json() as Me;
  }

  /**
   * Sign out — clear the session cookie server-side.
   *
   * The server has answered this route since identity was built and NO CLIENT EVER CALLED IT, so
   * until now there was no way to sign out of this product at all. That matters most on a machine
   * somebody shares: a reader who cannot leave is a reader whose account stays open behind them.
   *
   * The cookie is `httpOnly`, so only the server can clear it — a client-side "forget the user"
   * would leave the real session intact and lie about it.
   */
  async signOut(): Promise<void> {
    const response = await this.doFetch(join(this.apiBase, 'auth/logout'), this.authorize({ method: 'POST' }));
    if (!response.ok) throw await this.refused(response, 'could not sign out');
  }

  /**
   * ERASE THIS ACCOUNT — the right to leave, which no client could exercise.
   *
   * The server has answered `DELETE /auth/me` since the identity plane was built, and it does a
   * great deal of careful work: the reader's posts, their Discord channels, their studies (with a
   * retraction sent to any community that was told about them), the Halls they own and everything
   * those were holding — and then the reader and their cookie. Rooms are deliberately left standing
   * because other people are in them, and the reader is taken out of every one that names them.
   *
   * **No client had a method for it.** So the whole of that existed, was tested, and could not be
   * reached by a reader on any screen: the one act this product owes somebody who wants to stop
   * being part of it. Found by asking which server routes no client can call — the same question
   * that found `logout`, `unbanFromRoom` and the reports queue, each of which was reachable from
   * nowhere for the same reason.
   *
   * The session goes with the account, so nothing here needs a sign-out afterwards; the counts come
   * back for a caller that wants to say what was taken.
   */
  async eraseMe(): Promise<{ posts: number; reports: number; channels: number; sessions: number; halls: number; rooms: number }> {
    const response = await this.doFetch(join(this.apiBase, 'auth/me'), this.authorize({ method: 'DELETE' }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not erase your account');
    return await response.json() as { posts: number; reports: number; channels: number; sessions: number; halls: number; rooms: number };
  }

  /*
   * HALLS (COMMUNITY.md §4)
   */

  /** Public Halls, liveliest first — anonymous. */
  async listHalls(): Promise<HallView[]> {
    const response = await this.doFetch(join(this.apiBase, 'halls'));
    if (!response.ok) throw new Error('could not read the communities (' + response.status + ')');
    return await response.json() as HallView[];
  }

  /**
   * One Hall by its address — anonymous, because a leader links to this page from YouTube and
   * putting it behind an account would make it useless for the one job it has.
   */
  async hall(slug: string): Promise<HallView | null> {
    const response = await this.doFetch(join(this.apiBase, `halls/${encodeURIComponent(slug)}`));
    /*
     * 404 IS AN ANSWER; anything else is a failed read, and they were the same value.
     *
     * "There is no hall at that address" is what a mistyped link looks like, and it is what the
     * page says when this is null. It was also what a leader saw when the request simply failed:
     * the product telling somebody the community they built does not exist. That is the empty-
     * versus-unread defect one step worse, because the claim is about a thing they made rather
     * than about a list being short.
     */
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('could not read that community (' + response.status + ')');
    return await response.json() as HallView;
  }

  /** The Halls this reader belongs to — owned or joined. Signed-in. */
  async myHalls(): Promise<HallView[]> {
    const response = await this.doFetch(join(this.apiBase, 'halls/mine'), this.authorize({}));
    if (!response.ok) throw new Error('could not read your communities (' + response.status + ')');
    return await response.json() as HallView[];
  }

  /*
   * THE DISCORD BRIDGE, from the client's side — which had no client side at all.
   *
   * `COMMUNITY.md §6` calls integration "the mechanism that makes D20 real rather than rhetorical",
   * and the server has answered `GET/POST/DELETE bridge/hooks` since it was built: SSRF gate, a cap
   * per reader, a credential it refuses to hand back, a dead channel that retires itself. No client
   * reached any of it, so **no leader could connect their Discord** — the one integration Rule 3
   * actually names existed end-to-end except for the end a person touches.
   */

  /** The channels this reader has connected. Signed-in; an empty list is not an error. */
  async channels(): Promise<ChannelView[]> {
    const response = await this.doFetch(join(this.apiBase, 'bridge/hooks'), this.authorize({}));
    if (!response.ok) throw new Error('could not read the channels this community has connected (' + response.status + ')');
    return await response.json() as ChannelView[];
  }

  /** Connect one. The server refuses anything that is not a Discord channel webhook URL. */
  async addChannel(channel: NewChannel): Promise<ChannelView> {
    const response = await this.doFetch(join(this.apiBase, 'bridge/hooks'), this.authorize({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(channel),
    }));
    if (response.status === 401) throw new SocialAuthError();
    /* "that is not a Discord channel webhook URL" and "remove one of your 5 channels" are both
       things the reader can act on, and a pasted URL is exactly where an explanation is needed. */
    if (!response.ok) throw await this.refused(response, 'could not connect that channel');
    return await response.json() as ChannelView;
  }

  /**
   * CONNECT A RETIRED ONE AGAIN — the inverse retirement never had.
   *
   * A channel retires itself after five consecutive failed deliveries, which is right for a webhook
   * deleted in Discord and wrong for an evening of Discord being down: the streak clears only on a
   * success, so their outage retires a channel that was never broken. The way back used to be a
   * ceremony — delete the row, find the URL again (we never show it, it is a credential), paste it,
   * re-choose the passages — and this is that, as one tap on the row that already says "retired".
   */
  async reconnectChannel(channelId: string): Promise<ChannelView> {
    const response = await this.doFetch(join(this.apiBase, `bridge/hooks/${encodeURIComponent(channelId)}/reconnect`), this.authorize({
      method: 'POST',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not reconnect that channel');
    return await response.json() as ChannelView;
  }

  /** Disconnect one — only the reader's own, which the server enforces in the query. */
  async removeChannel(channelId: string): Promise<void> {
    const response = await this.doFetch(join(this.apiBase, `bridge/hooks/${encodeURIComponent(channelId)}`), this.authorize({
      method: 'DELETE',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not disconnect that channel');
  }

  /*
   * `hallMembers` WAS HERE, and nothing has ever called it — found by asking which promises this
   * client can make that no screen makes.
   *
   * It read the PUBLIC roster: ids, no names, because a named public member list is the
   * platform-wide social graph D21 refuses. That leaves a list of 24-character hexadecimal strings,
   * which this file already says elsewhere "is not a feature anybody can use" — so it could not be
   * rendered, and it never was. The useful public fact about a community is its SIZE, and that is
   * now on `HallView` as `memberCount`, counted server-side where it cannot drift.
   *
   * The route itself stays: it is a live v1 endpoint, the drive holds it to D21 by proving it
   * carries no names, and retiring a public URL is a decision for whoever owns the API's promises,
   * not a side effect of deleting a method nobody used.
   */

  /**
   * Who is waiting to be let into a Hall — its leaders only.
   *
   * Without this, `memberPolicy: 'approval'` is a trap rather than a setting: the public roster
   * lists active members only, so an applicant appears nowhere and the leader is never shown a
   * queue to act on.
   */
  /**
   * A page at a time, read forwards — see `goingOnFrom`. Approve and refuse are drawn on a ROW, so an applicant past the first page waited for ever — the state this read was written to end.
   */
  async hallPending(
    hallId: string,
    options: { after?: string; afterId?: string } = {},
  ): Promise<HallManagedMemberView[]> {
    const response = await this.doFetch(
      join(this.apiBase, `halls/${encodeURIComponent(hallId)}/members/pending${goingOnFrom(options)}`),
      this.authorize({}),
    );
    if (response.status === 401) throw new SocialAuthError();
    /*
     * NOT TOLERANT, and it used to be. A read that FAILED answered as a list with nothing in it,
     * which is the defect this layer has recorded more often than any other — and the panel above
     * had already been fixed for it, so the fix could never fire.
     *
     * a queue that could not be READ drew as a door nobody is knocking at, and the leader was
     * shown no sign of either. The panel has said "could not read" beside this list since the day it
     * was built; this method made that line unreachable
     */
    if (!response.ok) throw new Error('could not read who is waiting at the door (' + response.status + ')');
    return await response.json() as HallManagedMemberView[];
  }

  /**
   * The roster a leader runs their Hall from — active members, WITH NAMES.
   *
   * Distinct from `hallMembers`, which is the public roster and carries ids: choosing a co-host by
   * 24 hexadecimal characters is not a feature anybody can use, and publishing names on the public
   * one would build the platform-wide social graph D21 refuses by accident.
   */
  /**
   * WHAT THIS HALL'S MODERATORS HAVE DONE (COMMUNITY.md D22) — leadership only.
   *
   * D22 asks for the mod team and the record of it in one sentence: *"Leaders running a team need
   * this to supervise their own moderators; it is also what makes a disputed ejection resolvable."*
   * The team was built; the record was the one item on that list with nothing behind it.
   *
   * An error is an EMPTY log rather than a thrown one, the same as the roster beside it: a page
   * that fails to load one section should show the rest, and a leader who is not leadership here
   * simply sees nothing — which is also what a 403 means.
   */
  /**
   * A Hall's moderation log, newest first — and `before` walks BACK through it.
   *
   * The cursor is the `at` of the oldest row already held, so a page is "everything older than what
   * I have". Not an offset: the log grows at the newest end while it is being read, and a skip
   * count would show one act twice and hide another every time a moderator acts mid-read.
   */
  /** The same cursor as `siteLog`, for the same reason — see it there. */
  async hallLog(hallId: string, options: { before?: string; beforeId?: string } = {}): Promise<HallLogEntry[]> {
    const query = walkingBackFrom(options);
    const response = await this.doFetch(
      join(this.apiBase, `halls/${encodeURIComponent(hallId)}/manage/log${query}`),
      this.authorize({}),
    );
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw new Error("could not read what this community's team has done (" + response.status + ')');
    return await response.json() as HallLogEntry[];
  }

  /**
   * A page at a time, read forwards — see `goingOnFrom`. Barring somebody and making them a co-host are drawn on a ROW, so a member past the first page could be neither, by anybody.
   */
  async hallRoster(
    hallId: string,
    options: { after?: string; afterId?: string } = {},
  ): Promise<HallManagedMemberView[]> {
    const response = await this.doFetch(
      join(this.apiBase, `halls/${encodeURIComponent(hallId)}/manage/members${goingOnFrom(options)}`),
      this.authorize({}),
    );
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw new Error('could not read who is in this community (' + response.status + ')');
    return await response.json() as HallManagedMemberView[];
  }

  /** Let a waiting member in — leaders only. */
  async approveMember(hallId: string, userId: string): Promise<HallMemberView> {
    const response = await this.doFetch(
      join(this.apiBase, `halls/${encodeURIComponent(hallId)}/members/${encodeURIComponent(userId)}/approve`),
      this.authorize({ method: 'POST' }),
    );
    if (response.status === 401) throw new SocialAuthError();
    if (response.status === 403) throw await this.forbidden(response, new NotAHostError());
    if (!response.ok) throw await this.refused(response, 'could not approve that member');
    return await response.json() as HallMemberView;
  }

  /**
   * Appoint or stand down a co-host — the Hall's OWNER only.
   *
   * This is the delegation D22 calls the point of a Hall: a co-host moderates the Hall's rooms
   * without anyone hosting each one by hand. It existed as REST and as nothing else, so no leader
   * could ever have a mod team.
   */
  async setHallModerator(hallId: string, userId: string, isModerator: boolean): Promise<HallView> {
    /* Through `hallWrite` so the server's own sentence reaches the owner. This threw
       `could not change that co-host (400)` until the co-host CEILING existed, at which point a
       generic 400 became a lie of omission: the useful half — that the team is full and which
       one to stand down — was being discarded by the client that asked for it. */
    const path = `halls/${encodeURIComponent(hallId)}/moderators/${encodeURIComponent(userId)}`;
    return await this.hallWrite(path, isModerator ? 'POST' : 'DELETE');
  }

  /**
   * BAR SOMEBODY FROM THIS COMMUNITY — leadership only, and until now unreachable from anywhere.
   *
   * The server has answered this route since Halls existed, `admits` reads the list on every token
   * mint so a barred reader cannot enter any of the Hall's rooms, and banning ejects them from the
   * live ones. **No client could call it.** A leader's most serious control over their own
   * community was a route with nothing pointing at it — the defect this layer keeps producing, on
   * the thing `COMMUNITY.md` is entirely about.
   *
   * Hall-scoped, never site-wide: barring somebody from your study says nothing about them anywhere
   * else, which is the promise D22 makes to leaders and to the people they turn away.
   */
  async banFromHall(hallId: string, userId: string): Promise<void> {
    const path = `halls/${encodeURIComponent(hallId)}/members/${encodeURIComponent(userId)}/ban`;
    await this.hallWrite(path, 'POST');
  }

  /** Lift it. The list below is what makes this reachable — a barred reader is no longer a member. */
  async unbanFromHall(hallId: string, userId: string): Promise<void> {
    const path = `halls/${encodeURIComponent(hallId)}/members/${encodeURIComponent(userId)}/ban`;
    await this.hallWrite(path, 'DELETE');
  }

  /**
   * Whom this community has barred — its own leadership only.
   *
   * `HallView` omits `bannedIds` on purpose, so without this a leader cannot see whom they barred
   * and has nobody to name: the ban would be a one-way door by accident, exactly as a room's was.
   */
  async hallBanned(hallId: string): Promise<BannedReaderView[]> {
    const response = await this.doFetch(
      join(this.apiBase, `halls/${encodeURIComponent(hallId)}/manage/banned`),
      this.authorize({}),
    );
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw new Error('could not read who this community has barred (' + response.status + ')');
    return await response.json() as BannedReaderView[];
  }

  async createHall(hall: NewHall): Promise<HallView> {
    return this.hallWrite('halls', 'POST', hall);
  }

  async editHall(hallId: string, edits: HallEdits): Promise<HallView> {
    return this.hallWrite(`halls/${encodeURIComponent(hallId)}`, 'PATCH', edits);
  }

  /**
   * WHERE THIS READER STANDS with one Hall, so a page can offer the one control that applies.
   *
   * Answers `'none'` for a stranger and for anybody signed out, because a page that cannot tell
   * which reader is asking should offer to join — the only control that makes sense to somebody
   * with no account. A refusal is not an error worth throwing here: nothing depends on it beyond
   * which button is drawn.
   */
  async hallStanding(hallId: string): Promise<HallStanding> {
    const response = await this.doFetch(
      join(this.apiBase, `halls/${encodeURIComponent(hallId)}/membership`),
      this.authorize({}),
    );
    /*
     * `none` FOR A STRANGER AND FOR ANYBODY SIGNED OUT, which is what the comment above argues —
     * and it was also the answer when the read simply failed, which is a different thing.
     *
     * This decides the ONE control the page offers, so a member whose read failed was offered
     * "Join" the community they are already in. `hallControl` is a total function over a standing
     * it is entitled to believe; giving it a made-up one moves the lie out of sight.
     */
    if (response.status === 401 || response.status === 403 || response.status === 404) {
      return { status: 'none', role: null };
    }
    if (!response.ok) throw new Error('could not read where you stand with that community (' + response.status + ')');
    return await response.json() as HallStanding;
  }

  async joinHall(hallId: string): Promise<HallMemberView> {
    const response = await this.doFetch(join(this.apiBase, `halls/${encodeURIComponent(hallId)}/join`), this.authorize({
      method: 'POST',
    }));
    if (response.status === 401) throw new SocialAuthError();
    /* Banned, or full — the server gives ONE answer, and so does this. */
    if (response.status === 403) throw await this.forbidden(response, new RoomClosedToYouError());
    /* The deliberate vagueness is the LINE ABOVE, which is where banned and full are made to look
       alike. Everything that gets past it — a rate limit, a bad id — is ordinary and may speak. */
    if (!response.ok) throw await this.refused(response, 'could not join');
    return await response.json() as HallMemberView;
  }

  async leaveHall(hallId: string): Promise<void> {
    await this.hallAction(`halls/${encodeURIComponent(hallId)}/leave`, 'POST');
  }

  /** Ask for a verification token. It comes back once, to the owner (§3.1). */
  async startHallVerification(hallId: string, handle: string): Promise<HallChallenge> {
    const response = await this.doFetch(join(this.apiBase, `halls/${encodeURIComponent(hallId)}/verify`), this.authorize({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ handle }),
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (response.status === 403) throw await this.forbidden(response, new NotAHostError());
    /* This one guessed WRONG, not merely vaguely: "another hall has already verified that channel"
       is not "that is not a channel address", and a leader whose channel was claimed by somebody
       else needs to know that is what happened. */
    if (!response.ok) throw await this.refused(response, 'that is not a channel address');
    return await response.json() as HallChallenge;
  }

  /** Check it. A 400 means the token is not on the channel yet, which is the normal first answer. */
  async checkHallVerification(hallId: string): Promise<HallView> {
    return this.hallWrite(`halls/${encodeURIComponent(hallId)}/verify/check`, 'POST');
  }

  /**
   * Everything the owner may take with them (D21.3).
   *
   * An endpoint, not an appeal — and the client offers it plainly, because a door with no visible
   * lock is the point.
   */
  async exportHall(hallId: string): Promise<unknown> {
    const response = await this.doFetch(join(this.apiBase, `halls/${encodeURIComponent(hallId)}/export`), this.authorize({}));
    if (response.status === 401) throw new SocialAuthError();
    if (response.status === 403) throw await this.forbidden(response, new NotAHostError());
    if (!response.ok) throw await this.refused(response, 'could not export');
    return await response.json();
  }

  async deleteHall(hallId: string): Promise<void> {
    await this.hallAction(`halls/${encodeURIComponent(hallId)}`, 'DELETE');
  }

  /**
   * The error for a refused write, carrying the SERVER'S OWN SENTENCE where it wrote one.
   *
   * The server refuses with sixteen hand-written explanations — *a passphrase room needs a
   * passphrase*, *a session has to be in the future*, *another hall has already verified that
   * channel*, *close one of your 3 open rooms* — and every one of them tells the reader the next
   * thing to do. Most callers here replaced all of it with a status code, which is the same defect
   * as a control that does not exist: the answer was computed and then thrown away by the layer
   * that asked for it. Only the useful ones are routed through this; where a single vague answer
   * is DELIBERATE — joining a Hall, redeeming a code — that decision stands and is documented
   * where it is made.
   *
   * Only a STRING `message` is carried. Nest's validation pipe answers with an array of field
   * complaints ("startsAt must be a string"), which is written for whoever wrote the request, not
   * for whoever is reading the screen — those keep the fallback.
   */
  /**
   * A 403 THAT IS ABOUT THE READER, not about what they asked for.
   *
   * Eight paths turn a 403 into their own sentence — "only a host can do that", "this room is not
   * open to you" — because for those routes that is what a refusal means. For a SUSPENDED reader
   * every one of them is wrong in the same way: it describes the request when the answer is about
   * the account, and it sends somebody hunting for a room problem they do not have.
   *
   * The body is read HERE and the caller's error is passed in already made, so the vague answers
   * stay vague for the reasons they were vague about — banned, closed, wrong passphrase — and the
   * one refusal that should never be vague stops being so.
   */
  private async forbidden(response: SocialResponse, otherwise: Error): Promise<Error> {
    const detail = await response.json().catch(() => null) as { message?: unknown } | null;
    return detail?.message === ACCOUNT_SUSPENDED ? new SuspendedError() : otherwise;
  }

  private async refused(response: SocialResponse, fallback: string): Promise<RefusedError> {
    /*
     * A RATE LIMIT SPEAKS FOR ITSELF, and what it says by default is a class name. NestJS's
     * throttler answers `{"message": "ThrottlerException: Too Many Requests"}` — a string, so this
     * helper carried it happily onto a reader's screen the day the clients started trusting the
     * server's words. The server now writes a human sentence for it, and this stands whatever any
     * server says, because a 429 is one thing and the client already knows which.
     */
    if (response.status === 429) {
      return new RefusedError('that was a lot of requests at once — wait a moment and try again', 'tooFast');
    }
    /*
     * ASKED, NOT ASSERTED. This read the body and declared its shape with `as`, which is a promise
     * about a value that arrived from the network — the one kind of value nothing can promise. A
     * server that answers with a string, an array or `null` satisfied that type and nothing said so.
     *
     * The plane already narrows exactly this, twice, in the two other places it reads something
     * nobody wrote for us: `refusalOf` in the server's exception filter, and `readPayload` in the
     * voice connector. Both ask "does this object have a field called X" — a question TypeScript
     * can answer — and this is the third, now written the same way.
     */
    const body: unknown = await response.json().catch(() => null);
    const said = (field: string): unknown =>
      (typeof body === 'object' && body !== null && field in body ? Reflect.get(body, field) : undefined);

    /* The one refusal that is about the READER rather than about what they asked for. Recognised
       here so every write path gets it without knowing about it — see `SuspendedError`. */
    if (response.status === 403 && said('message') === ACCOUNT_SUSPENDED) return new SuspendedError();
    /*
     * The NAME the server gave this refusal, if it gave one and if it is a name this client knows.
     * An unknown name is treated as no name at all rather than trusted: a server newer than its
     * clients will send reasons they have no words for, and English prose is a better answer than a
     * key on the screen.
     */
    const named = said('reason');
    const message = said('message');
    return new RefusedError(
      typeof message === 'string' ? message : `${fallback} (${response.status})`,
      isRefusalReason(named) ? named : null,
      readValues(said('values')),
    );
  }

  private async hallWrite(path: string, method: string, body?: object): Promise<HallView> {
    const response = await this.doFetch(join(this.apiBase, path), this.authorize({
      method,
      ...(body ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) } : {}),
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (response.status === 403) throw await this.forbidden(response, new NotAHostError());
    /* A taken address, a reserved word, a token not yet on the channel — all explained by the
       server, and the explanation is the useful part. */
    if (!response.ok) throw await this.refused(response, 'that did not work');
    return await response.json() as HallView;
  }

  private async hallAction(path: string, method: string): Promise<void> {
    const response = await this.doFetch(join(this.apiBase, path), this.authorize({ method }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'that did not work');
  }

  /**
   * What is coming up on a node — anonymous, soonest first.
   *
   * Public because a session IS the invitation: a node showing "Thursday 20:00, Genesis 22" is
   * what fills a quiet map, and putting it behind an account would defeat the point.
   */
  /**
   * EVERY SESSION THIS READER HAS PROMISED, wherever on the map it sits. Signed-in.
   *
   * A leader may hold five scheduled sessions at once and, until this was called by anybody, could
   * see them only by walking to each node they happen to remember. That makes the cap unanswerable
   * — "cancel one of your 5 scheduled sessions" names no five — and makes a promise left somewhere
   * forgotten the normal case rather than the careless one.
   */
  async mySessions(): Promise<SessionView[]> {
    const response = await this.doFetch(join(this.apiBase, 'sessions/mine'), this.authorize({}));
    if (!response.ok) throw new Error('could not read your studies (' + response.status + ')');
    return await response.json() as SessionView[];
  }

  async listSessions(nodeId: string): Promise<SessionView[]> {
    const response = await this.doFetch(join(this.apiBase, `nodes/${encodeURIComponent(nodeId)}/sessions`));
    if (!response.ok) throw new Error('could not read the studies on this passage (' + response.status + ')');
    return await response.json() as SessionView[];
  }

  /**
   * A Hall's upcoming sessions, across every node.
   *
   * The half of a Hall's page that is true when nobody happens to be talking — which is most of the
   * time, and is why this matters more than the live-room list beside it.
   */
  async listHallSessions(hallId: string): Promise<SessionView[]> {
    const response = await this.doFetch(join(this.apiBase, `halls/${encodeURIComponent(hallId)}/sessions`));
    /*
     * NOT TOLERANT, and it used to be. A read that FAILED answered as a list with nothing in it,
     * which is the defect this layer has recorded more often than any other — and the panel above
     * had already been fixed for it, so the fix could never fire.
     *
     * the panel keeps `sessionsUnread` for exactly this and says so in its own comment — *"kept,
     * like every other list on this page that says when it failed"* — and could never set it
     */
    if (!response.ok) throw new Error('could not read this community\'s studies (' + response.status + ')');
    return await response.json() as SessionView[];
  }

  /** Schedule one — signed-in. */
  async scheduleSession(nodeId: string, session: NewSession): Promise<SessionView> {
    const response = await this.doFetch(join(this.apiBase, `nodes/${encodeURIComponent(nodeId)}/sessions`), this.authorize({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(session),
    }));
    if (response.status === 401) throw new SocialAuthError();
    /* "that is not a time", "a session has to be in the future", "that is not a time zone" — a
       leader fixing a form needs to be told which of the three, and scheduling is the highest-value
       thing a leader does here. */
    if (!response.ok) throw await this.refused(response, 'could not schedule that');
    return await response.json() as SessionView;
  }

  /**
   * A NEW WORD for a study held behind one — the rotation a room has had since it could lock.
   *
   * A leaked passphrase was permanent for the life of a weekly study: the only remedy was to cancel
   * it and schedule another, which tells the leader's Discord the study is OFF and then announces a
   * second one, for a study that never stopped happening. The door itself cannot be changed here —
   * the notice already sent says which door it has, in a channel we cannot edit.
   */
  async rekeyStudy(sessionId: string, passphrase: string): Promise<SessionView> {
    const response = await this.doFetch(join(this.apiBase, `sessions/${encodeURIComponent(sessionId)}/passphrase`), this.authorize({
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ passphrase }),
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not change that passphrase');
    return await response.json() as SessionView;
  }

  /** Call one off — signed-in, and only the caller's own. */
  async cancelSession(sessionId: string): Promise<SessionView> {
    const response = await this.doFetch(join(this.apiBase, `sessions/${encodeURIComponent(sessionId)}`), this.authorize({
      method: 'DELETE',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not cancel that');
    return await response.json() as SessionView;
  }

  /**
   * THE ROOMS THIS READER HAS OPEN, wherever they are — the list the three-room limit needed.
   *
   * A room stops being listed on its node two minutes after the last person leaves, and goes on
   * counting against the limit until the idle sweep takes it up to an hour later. The refusal in
   * between says *"close one of your 3 open rooms before opening another"*, which named an act the
   * reader could not perform: nothing in the product would show them the three.
   *
   * Signed-in, and an empty list is the ordinary answer rather than an error — most readers have no
   * rooms open and the panel simply shows nothing.
   */
  async myRooms(): Promise<RoomView[]> {
    const response = await this.doFetch(join(this.apiBase, 'rooms/mine'), this.authorize({}));
    if (response.status === 401) throw new SocialAuthError();
    /*
     * NOT TOLERANT, and it used to be. A read that FAILED answered as a list with nothing in it,
     * which is the defect this layer has recorded more often than any other — and the panel above
     * had already been fixed for it, so the fix could never fire.
     *
     * it is not a shortcut when a reader has just been refused a fourth room and told to close one
     * of three: the panel says the list is missing rather than empty, which is the corner this
     * section exists to get them out of
     */
    if (!response.ok) throw new Error('could not read the rooms you have open (' + response.status + ')');
    return await response.json() as RoomView[];
  }

  /**
   * WHO IS WHERE, ACROSS THE WHOLE MAP — counts only, and never names.
   *
   * The map draws a figure somebody is standing at differently: a ring of seats around it, and a
   * pulse when one of them has a live voice. That needs presence for every figure at once, which
   * `listRooms` cannot answer without being asked a thousand times.
   *
   * IT CARRIES NO NAMES AND NO ROOM TITLES, deliberately. Being in a public room is consent to be in
   * that room, not to be plotted on a map of where everybody is standing. The engine draws seats
   * from a count, so a count is all it is given.
   *
   * An empty list is not an error, and neither is a deployment with no social plane at all — a map
   * with nobody on it is a perfectly ordinary map.
   */
  async mapPresence(): Promise<{ nodeId: string; readers: number; voice: boolean }[]> {
    const response = await this.doFetch(join(this.apiBase, 'rooms/presence'));
    /* Tolerant: a deployment with no social plane answers this way, and that is a map with nobody on it. */
    if (!response.ok) return [];
    return await response.json()
      .then(body => body as { nodeId: string; readers: number; voice: boolean }[])
      /* A 200 that is not JSON is still nobody — see `room` below for where that came from. */
      .catch(() => []);
  }

  /** The rooms open on a node — anonymous, liveliest first. An empty list is not an error. */
  async listRooms(nodeId: string): Promise<RoomView[]> {
    const response = await this.doFetch(join(this.apiBase, `nodes/${encodeURIComponent(nodeId)}/rooms`));
    /*
     * NOT TOLERANT — and the tolerance was right, in one of the two places this is read from.
     *
     * It answered every failure with an empty list, for a reason written here and true: *the
     * walk-in must not be stood between a reader and a conversation they have already been given
     * a token for*. But the LISTING is read by the panel that exists to bring readers into a
     * conversation, and that panel says — in its own comment, above a `catch` that could never
     * run — *"No one is talking here yet is a claim about whether a conversation is happening,
     * and a failed read was making it. Somebody who would have joined instead sees a quiet
     * passage and leaves."*
     *
     * **Two deliberate decisions, each documented, that contradicted each other**: one method
     * answering two questions. So the answer is honest here, and the tolerance moved to the four
     * call sites whose reason it actually is — where it is one line and says so.
     */
    if (!response.ok) throw new Error('could not read who is talking here (' + response.status + ')');
    return await response.json() as RoomView[];
  }

  /**
   * The rooms open under a Hall, across every node — what its page shows.
   *
   * The only room list in the product that crosses the map rather than sitting on one point of it:
   * a community's page answers "where are we talking right now", and the answer is usually somewhere
   * the reader was not already looking.
   */
  async listHallRooms(hallId: string): Promise<RoomView[]> {
    const response = await this.doFetch(join(this.apiBase, `halls/${encodeURIComponent(hallId)}/rooms`));
    /* Tolerant: a community with nothing live is the ordinary case. */
    if (!response.ok) return [];
    return await response.json() as RoomView[];
  }

  /**
   * ONE ROOM, BY ID — anonymous, and null when there is no such room.
   *
   * The list is not a substitute for this and cannot be. A room that is empty and older than its
   * two-minute grace is deliberately absent from `listRooms` (`GROWTH.md §4`: an empty room on the
   * map is a promise the product visibly fails to keep) while remaining perfectly live and
   * perfectly joinable — which is exactly the state a scheduled study is in between the moment its
   * room opens and the moment the first person arrives.
   */
  async room(roomId: string): Promise<RoomView | null> {
    /*
     * SIGNED AS WHOEVER IS ASKING, though anyone may ask — and this read was anonymous.
     *
     * The route is public and stays public: a reader with no account gets the same room they always
     * did. But the answer is not the same for everybody. `toView(room, forHost)` carries the INVITE
     * CODE only to a host, and an unauthenticated read is not a host, so this returned a strictly
     * poorer room than the caller was entitled to — silently, since a missing optional field looks
     * exactly like a room that has no code.
     *
     * It cost the invite code off a host's screen. `ActiveRoom` re-reads the room whenever somebody
     * nudges it, replacing what it holds; the copy it had came from the response to LOCKING the
     * door and carried the code, and the copy that replaced it did not. So a host locked a raided
     * room, was shown the code that is the whole way back in, and watched it vanish the next time
     * anything in the room changed. Found by the two-browser gate the day the server began
     * announcing changes — which made the re-read happen to the host as well, every time, rather
     * than only when somebody else moved.
     *
     * `authorize` is safe on a public route: signed out, it adds nothing at all.
     */
    const response = await this.doFetch(join(this.apiBase, `rooms/${encodeURIComponent(roomId)}`), this.authorize({}));
    /*
     * 404 IS AN ANSWER — the room went — and everything else is a failed read. They were one.
     *
     * The panel reads *"still nothing means the room went while we were asking"* off this null
     * and tells the reader they cannot join. So a server hiccup accused somebody of being shut
     * out of a conversation that was still going on: the same shape as a community reported
     * missing, on the sentence a reader is least able to argue with.
     */
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('could not read that room (' + response.status + ')');
    /*
     * A 200 THAT IS NOT JSON IS STILL A NO. Any single-page host answers an unknown path with
     * `index.html` and a 200 rather than a 404 — so a misconfigured API base makes this resolve to
     * a page of markup, `json()` throws a SyntaxError, and a read that promised null on failure
     * rejects instead. That is not hypothetical: it is what the browser gate hit, and the throw
     * travelled up into the walk-in path and stopped a reader joining at all.
     */
    return await response.json().then(body => body as RoomView).catch(() => null);
  }

  /** Open a room on a node — signed-in. */
  async createRoom(nodeId: string, room: NewRoom): Promise<RoomView> {
    const response = await this.doFetch(join(this.apiBase, `nodes/${encodeURIComponent(nodeId)}/rooms`), this.authorize({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(room),
    }));
    if (response.status === 401) throw new SocialAuthError();
    /* The quota refusal is the one that matters: "close one of your 3 open rooms before opening
       another" names the action, and a reader told "could not open the room (400)" has no idea
       they already have three — least of all that the sweeper will not reclaim them fast enough. */
    if (!response.ok) throw await this.refused(response, 'could not open the room');
    return await response.json() as RoomView;
  }

  /**
   * A token to join one room — signed-in, and carrying only what this reader may do there.
   *
   * A 403 here is a room that will not have them: banned, or closed. It is deliberately NOT a
   * SocialAuthError, because offering "sign in to join" to someone already signed in and barred is
   * both confusing and useless.
   */
  async roomToken(roomId: string, secrets: RoomSecrets = {}): Promise<VoiceToken> {
    const response = await this.doFetch(join(this.apiBase, `rooms/${encodeURIComponent(roomId)}/token`), this.authorize({
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(secrets),
    }));
    if (response.status === 401) throw new SocialAuthError();
    /* 403 stays deliberately vague: banned, closed, wrong passphrase and invite-only answer alike,
       so a refusal cannot be used as an oracle. That decision is made server-side and stands. */
    if (response.status === 403) throw await this.forbidden(response, new RoomClosedToYouError());
    /*
     * EVERYTHING ELSE CARRIES THE SERVER'S SENTENCE. This threw a bare `Error`, which the web panel
     * has no shape for — so the one failure a single self-hosted SFU makes most likely, the room
     * server being unreachable, arrived as a 503 with a written explanation and was rendered as
     * NOTHING AT ALL. `walkIn` beside this already did the right thing; the named-room path did not,
     * which is the same defect in the same file, one method apart.
     */
    if (!response.ok) throw await this.refused(response, 'could not join the room');
    return await response.json() as VoiceToken;
  }

  /**
   * PUT ME IN A ROOM ON THIS NODE — joins the conversation already happening, or starts one.
   *
   * The path almost everyone takes: wanting to talk about Abraham is not the same as wanting to
   * decide whether to create or join.
   */
  async walkIn(nodeId: string): Promise<VoiceToken> {
    const response = await this.doFetch(join(this.apiBase, `nodes/${encodeURIComponent(nodeId)}/rooms/join`), this.authorize({
      method: 'POST',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (!response.ok) throw await this.refused(response, 'could not open a room');
    return await response.json() as VoiceToken;
  }

  /**
   * Enter an unlisted room by its code.
   *
   * A wrong code and a room that will not have you answer identically, on purpose — so this reports
   * one thing, and the UI must not try to tell the reader which it was.
   */
  async joinByInvite(code: string): Promise<VoiceToken> {
    const response = await this.doFetch(join(this.apiBase, `rooms/invite/${encodeURIComponent(code)}`), this.authorize({
      method: 'POST',
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (response.status === 404) throw new RoomClosedToYouError();
    if (!response.ok) throw await this.refused(response, 'could not join the room');
    return await response.json() as VoiceToken;
  }

  /** Close a room — hosts only. */
  async closeRoom(roomId: string): Promise<RoomView> {
    return this.hostAction(`rooms/${encodeURIComponent(roomId)}/close`, 'POST');
  }

  /**
   * CHANGE WHO MAY COME IN, and rotate the secret by doing it (D24) — hosts only.
   *
   * The answer to a raid, which ban and remove could only fight one arrival at a time; and the
   * answer to an invite code that ended up somewhere public, which nothing could replace. Setting
   * `invite` again mints a fresh code — rotation and locking are one act because they are one
   * decision. The reply carries the new code, since a host who cannot read it cannot use it.
   *
   * Nobody already in the room is removed: the door is about arrivals.
   */
  async setRoomAccess(roomId: string, access: RoomAccess, passphrase?: string): Promise<RoomView> {
    return this.hostAction(`rooms/${encodeURIComponent(roomId)}/access`, 'POST', { access, ...(passphrase ? { passphrase } : {}) });
  }

  /** Force-mute someone's live microphone — hosts only. They may unmute themselves. */
  async muteParticipant(roomId: string, userId: string): Promise<void> {
    await this.hostAction(this.participantPath(roomId, userId, 'mute'), 'POST');
  }

  /** Promote to, or take off, the stage — hosts only. */
  async setOnStage(roomId: string, userId: string, onStage: boolean): Promise<RoomView> {
    return this.hostAction(this.participantPath(roomId, userId, 'stage'), 'POST', { onStage });
  }

  /** Eject someone now. Durable refusal is `banFromRoom`. */
  async removeParticipant(roomId: string, userId: string): Promise<void> {
    await this.hostAction(this.participantPath(roomId, userId, 'remove'), 'POST');
  }

  /** Eject AND refuse re-entry — hosts only. */
  async banFromRoom(roomId: string, userId: string): Promise<RoomView> {
    return this.hostAction(this.participantPath(roomId, userId, 'ban'), 'POST');
  }

  /**
   * Take every microphone in the room except the hosts'.
   *
   * The other controls are per-person, which is right for one noisy participant and useless for a
   * leader in front of three hundred. This revokes the GRANT rather than muting the track, so the
   * quiet holds until the host hands the microphone back with `setOnStage` — one person at a time,
   * which is the shape a taught session has.
   */
  /**
   * ASK TO SPEAK (COMMUNITY.md D22) — the one room control that is not a host's.
   *
   * A listener in a room of three hundred could previously only wait to be noticed by somebody
   * addressing three hundred people, which is not a queue but luck. Asking twice changes nothing:
   * the server uses `$addToSet`, so tapping repeatedly cannot move anybody up the line.
   *
   * Not `hostAction` — this needs no authority, and routing it through a helper named for one would
   * be the wrong sentence in the wrong place.
   */
  async raiseHand(roomId: string): Promise<RoomView> {
    const response = await this.doFetch(join(this.apiBase, `rooms/${encodeURIComponent(roomId)}/hand`), this.authorize({
      method: 'POST',
    }));
    if (response.status === 401) throw new SocialAuthError();
    /* Closed, or barred — one answer for both, as the token mint gives. */
    if (response.status === 403) throw await this.forbidden(response, new RoomClosedToYouError());
    if (!response.ok) throw await this.refused(response, 'could not ask to speak');
    return await response.json() as RoomView;
  }

  /**
   * ASK A SECOND HALL TO CO-HOST THIS ROOM (COMMUNITY.md D22) — two leaders, one session.
   *
   * Proposing attaches nothing: until that Hall's leadership accepts, the room lists exactly where
   * it did and their leadership moderates nothing. That is the whole design — a field a leader
   * could simply set would let anybody put a respected community's name beside their own study,
   * which is the impersonation problem `COMMUNITY.md §3.1` calls specific to this domain.
   */
  async proposeCoHost(roomId: string, hallId: string): Promise<RoomView> {
    return this.hostAction(`rooms/${encodeURIComponent(roomId)}/cohost/${encodeURIComponent(hallId)}`, 'POST');
  }

  /**
   * WHO HAS ASKED THIS HALL TO CO-HOST THEM — leadership only, and unanswered only.
   *
   * Without this, accepting is unreachable: a pending proposal is deliberately absent from the
   * public room list, so this is the only place the invited leadership learns they were asked.
   *
   * An error is an empty list, like the roster and the log beside it: a page that fails to load one
   * section should show the rest, and a reader who is not leadership here simply sees nothing.
   */
  /**
   * A page at a time, walked back — because **accept and decline exist nowhere else**.
   *
   * An invitation past the first page is a question this leadership can never answer, and
   * nothing bounds how many rooms may ask them at once. `RoomView` already carries the two
   * halves of the cursor, so this needed no new field: `createdAt` and `id`.
   */
  async coHostInvitations(
    hallId: string,
    options: { before?: string; beforeId?: string } = {},
  ): Promise<RoomView[]> {
    const response = await this.doFetch(
      join(this.apiBase, `halls/${encodeURIComponent(hallId)}/rooms/invitations${walkingBackFrom(options)}`),
      this.authorize({}),
    );
    if (response.status === 401) throw new SocialAuthError();
    /*
     * NOT TOLERANT, and it used to be. A read that FAILED answered as a list with nothing in it,
     * which is the defect this layer has recorded more often than any other — and the panel above
     * had already been fixed for it, so the fix could never fire.
     *
     * the panel already says it: *"the asking going silent renders as nobody having asked — which
     * is nearly always true and therefore never questioned"*. It kept a state for that and this
     * method meant nothing could reach it
     */
    if (!response.ok) throw new Error('could not read what this community has been asked (' + response.status + ')');
    return await response.json() as RoomView[];
  }

  /** Say yes, for the Hall you lead. Only its leadership can, which the server checks. */
  async acceptCoHost(roomId: string, hallId: string): Promise<RoomView> {
    return this.hostAction(`rooms/${encodeURIComponent(roomId)}/cohost/${encodeURIComponent(hallId)}/accept`, 'POST');
  }

  /** Decline, withdraw, or part ways — one call, and either leadership may make it. */
  async endCoHost(roomId: string): Promise<RoomView> {
    return this.hostAction(`rooms/${encodeURIComponent(roomId)}/cohost`, 'DELETE');
  }

  /** Put a hand down — your own always, somebody else's only if you host the room. */
  async lowerHand(roomId: string, userId: string): Promise<RoomView> {
    return this.hostAction(this.participantPath(roomId, userId, 'hand'), 'DELETE');
  }

  async silenceRoom(roomId: string): Promise<RoomView> {
    return this.hostAction(`rooms/${encodeURIComponent(roomId)}/silence`, 'POST');
  }

  /**
   * WHOM THIS ROOM HAS BARRED — to its own leadership, and to nobody else.
   *
   * The read that made `unbanFromRoom` reachable. `bannedIds` is deliberately absent from a room's
   * public view, so a host could not see whom they had banned and had nobody to name: a ban was a
   * one-way door by accident rather than by decision. This is not the public list that rule
   * forbids — it goes only to whoever may ban from this room, and tells them what they did.
   */
  async roomBanned(roomId: string): Promise<BannedReaderView[]> {
    const response = await this.doFetch(
      join(this.apiBase, `rooms/${encodeURIComponent(roomId)}/banned`),
      this.authorize({}),
    );
    if (response.status === 401) throw new SocialAuthError();
    /* An empty list for a reader who may not ask: the section simply does not appear, which is the
       same as it being empty, and neither reveals whether anybody is on it. */
    /*
     * NOT TOLERANT, and it used to be. A read that FAILED answered as a list with nothing in it,
     * which is the defect this layer has recorded more often than any other — and the panel above
     * had already been fixed for it, so the fix could never fire.
     *
     * THE WORST OF THE FIVE. `bannedIds` is absent from a room's view on purpose, so this list is
     * the only place a ban is visible and the only path to lifting one — and the panel says so at
     * length: emptying it drew "nobody is barred", the answer a host is most likely to accept
     * without question, and took the undo away with it
     */
    if (!response.ok) throw new Error('could not read who this room has barred (' + response.status + ')');
    return await response.json() as BannedReaderView[];
  }

  async unbanFromRoom(roomId: string, userId: string): Promise<RoomView> {
    return this.hostAction(this.participantPath(roomId, userId, 'ban'), 'DELETE');
  }

  /** Share hostship — hosts only. */
  async addHost(roomId: string, userId: string): Promise<RoomView> {
    return this.hostAction(`rooms/${encodeURIComponent(roomId)}/hosts/${encodeURIComponent(userId)}`, 'POST');
  }

  private participantPath(roomId: string, userId: string, action: string): string {
    return `rooms/${encodeURIComponent(roomId)}/participants/${encodeURIComponent(userId)}/${action}`;
  }

  /**
   * Every host control takes the same shape, so it is written once: signed-in, and a 403 means the
   * caller is not a host of this room — which the UI shows rather than a generic failure, because
   * "you are not a host" and "the network broke" call for different reactions.
   */
  private async hostAction(path: string, method: string, body?: object): Promise<RoomView> {
    const response = await this.doFetch(join(this.apiBase, path), this.authorize({
      method,
      ...(body ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) } : {}),
    }));
    if (response.status === 401) throw new SocialAuthError();
    if (response.status === 403) throw await this.forbidden(response, new NotAHostError());
    /*
     * AND THE REST CARRY WHAT THE SERVER SAID. This threw a bare `Error`, and the panel's catch
     * only knows `NotAHostError` — so it was swallowed and the host saw NOTHING.
     *
     * The case that makes it matter is the stage. `setStage` deliberately answers 403 "only a host
     * can change the stage" OR 400 "the stage is full (8)", and its comment says why: *distinguished
     * so a host can act on it*. The server took the trouble to tell two situations apart and the
     * client threw the second one away — so a leader promoting a ninth speaker clicked, and the
     * product did not react at all. `rooms.stageFull` was even written into all three dictionaries
     * for it, and rendered nowhere.
     *
     * The sentence carries the actual limit, which no client-side phrase could without keeping its
     * own copy of a number the server owns.
     */
    if (!response.ok) throw await this.refused(response, 'that did not work');
    return await response.json() as RoomView;
  }

  /** Where to send a reader to sign in with Google — a URL the browser opens, or a phone hands its OAuth flow. */
  signInUrl(): string {
    return join(this.apiBase, 'auth/google');
  }
}

/** The web's transport: the browser sends the httpOnly session cookie, so a write just opts in. */
export const cookieAuthorize: Authorize = init => ({ ...init, credentials: 'include' });

/** A phone's transport: attach the keychain token as a bearer, or nothing when signed out (→ 401 → sign-in). */
export function bearerAuthorize(getToken: () => string | null): Authorize {
  return init => {
    const token = getToken();
    return token ? { ...init, headers: { ...init.headers, authorization: `Bearer ${token}` } } : init;
  };
}
